import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Map as MapLibreMapType } from "maplibre-gl";
import {
  Download,
  Eye,
  EyeOff,
  Pause,
  Play,
  Plus,
  Square,
  Trash2,
  Upload,
} from "lucide-react";
import type { MeasureMode } from "@/components/map-libre/MapLibreMeasureTool";
import { signsInsideLasso } from "@/lib/visualizer-cad-geometry";
import { exportBoardAs, parseGisImportFile } from "@/tactical/io/boardGisIo";
import type { FeatureCreatePayload } from "@/tactical/io/boardPackage";
import {
  TacticalFeatureHistory,
  asFeatureSnapshot,
  snapshotToCreatePayload,
  snapshotToStylePatch,
} from "@/tactical/history/tactical-feature-history";
import { useTacticalHistoryPlayback } from "@/tactical/history/useTacticalHistoryPlayback";
import type { HistoryPlaybackDelayMs } from "@/tactical/history/historyPlayback";
import { TacticalLayersPanel } from "@/tactical/TacticalLayersPanel";
import { TacticalReferenceLayersPanel } from "@/tactical/TacticalReferenceLayersPanel";
import { TacticalHeatmapPanel } from "@/tactical/TacticalHeatmapPanel";
import { TacticalObjectBindPanel } from "@/tactical/TacticalObjectBindPanel";
import { TacticalFeatureCardPanel } from "@/tactical/TacticalFeatureCardPanel";
import { TacticalBoardMenu } from "@/tactical/shell/TacticalBoardMenu";
import { TacticalFeatureList } from "@/tactical/shell/TacticalFeatureList";
import { TacticalFeatureStyleForm } from "@/tactical/shell/TacticalFeatureStyleForm";
import { TacticalRightInspector } from "@/tactical/shell/TacticalRightInspector";
import { TacticalSideRail } from "@/tactical/shell/TacticalSideRail";
import { TacticalMapToolbar } from "@/tactical/shell/TacticalMapToolbar";
import {
  clampTacticalRailWidth,
  readRailCollapsed,
  readRailWidth,
  writeRailCollapsed,
  writeRailWidth,
} from "@/tactical/shell/tacticalSideRail";
import Header from "@/components/Header";
import { ModuleMapUiStubProvider } from "@/components/module-map/ModuleMapUiStubProvider";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuthV2";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { milSidcToDataUrl } from "@/lib/tactical-milsymbol";
import {
  tacticalApi,
  tacticalWsUrl,
  type TacticalBoard,
  type TacticalBoardMemberRole,
  type TacticalFeature,
  type TacticalSymbolMeta,
} from "@/services/tactical.service";

type CompareRole = "only_base" | "only_other" | "changed" | "unchanged";

const COMPARE_COLORS: Record<CompareRole, string> = {
  only_base: "#b91c1c",
  only_other: "#15803d",
  changed: "#c2410c",
  unchanged: "#94a3b8",
};

function tagCompare(f: TacticalFeature, role: CompareRole): TacticalFeature {
  const color = COMPARE_COLORS[role];
  const props = f.properties;
  if (!props) return f;
  return {
    ...f,
    properties: {
      ...props,
      strokeColor: color,
      fillColor: color,
      compareRole: role,
      effectivelyVisible: true,
      legendKey: `tactical:compare:${role}:${props.id}`,
    },
  };
}
import { buildOptimisticFeature } from "@/tactical/offline/buildOptimisticFeature";
import { flushTacticalQueue } from "@/tactical/offline/flushTacticalQueue";
import {
  enqueueMutation,
  getBoardFeaturesCache,
  listMutations,
  nextTempFeatureId,
  putBoardFeaturesCache,
} from "@/tactical/offline/tacticalOfflineDb";
import type { RemoteCursor } from "@/tactical/sync/tacticalAwareness";
import {
  createTacticalCollabRoom,
  type CollabDraft,
  type TacticalCollabRoom,
} from "@/tactical/sync/tacticalCollabRoom";
import { TacticalRemoteCursors } from "@/tactical/sync/TacticalRemoteCursors";
import { TacticalRemoteDrafts } from "@/tactical/sync/TacticalRemoteDrafts";
import { TacticalTracksOverlay } from "@/tactical/TacticalTracksOverlay";
import { computeLos, type LosResult } from "@/tactical/los/computeLos";
import { TacticalLosOverlay } from "@/tactical/los/TacticalLosOverlay";
import {
  ensureTacticalTerrain,
  queryGroundM,
  tacticalDemTilesUrl,
} from "@/tactical/los/tacticalTerrain";
import { computeViewshed, type ViewshedResult } from "@/tactical/viewshed/computeViewshed";
import { TacticalViewshedOverlay } from "@/tactical/viewshed/TacticalViewshedOverlay";
import {
  listTacticalPlugins,
  getTacticalPlugin,
} from "@/tactical/plugins";
import { circlePolygon } from "@/tactical/plugins/rangeRingGeometry";
import { TacticalRangeRingOverlay } from "@/tactical/plugins/TacticalRangeRingOverlay";
import { snapPointWithOptionalMvt } from "@/tactical/geo/snapPoint";
import { validatePolygonCoords } from "@/tactical/geo/validatePolygon";
import { buildBufferPolygon } from "@/tactical/geo/buildBuffer";
import { listBasemapRegions } from "@/tactical/basemap/tacticalBasemapRegionsDb";
import {
  defaultOsmTemplate,
  estimateTileCount,
  extractRasterTileTemplate,
  MAX_TILES,
  prefetchBasemapRegion,
  removeBasemapRegion,
} from "@/tactical/basemap/prefetchBasemapRegion";

const MapLibreMap = lazy(() => import("@/components/map-libre/MapLibreMap"));

type ToolMode =
  | "pan"
  | "select"
  | "point"
  | "line"
  | "polygon"
  | "text"
  | "freehand"
  | "vertex"
  | "scissors"
  | "rotate"
  | "lasso";
type CadBooleanMode = "union" | "difference" | null;

const DEFAULT_PIN = "/tactical-pin.svg";
const STROKE = "#dc2626";

function withMapIcons(features: TacticalFeature[]): TacticalFeature[] {
  return features.map((f) => {
    const props = f.properties;
    if (!props) return f;
    const kind = props.kind ?? "point";
    if (kind !== "point") return f;
    const sidc = (props.symbolSidc || "").trim();
    if (sidc) {
      return {
        ...f,
        properties: {
          ...props,
          iconUrl: milSidcToDataUrl(sidc, Math.round(props.sizePx || 35)),
        },
      };
    }
    if (!props.iconUrl) {
      return { ...f, properties: { ...props, iconUrl: DEFAULT_PIN } };
    }
    return f;
  });
}

export default function TacticalWorkspacePage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { hasPermission, user } = useAuth();
  const canManagePerm = hasPermission("tactical.manage");
  const localUserId = String((user as { id?: string | number } | undefined)?.id ?? "");
  const localUserLabel =
    String(
      (user as { username?: string; email?: string; full_name?: string } | undefined)?.username ||
        (user as { email?: string } | undefined)?.email ||
        localUserId.slice(0, 8),
    ) || "я";

  const [boardId, setBoardId] = useState<number | null>(null);
  const [toolMode, setToolMode] = useState<ToolMode>("select");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const selectedId = selectedIds[0] ?? null;
  const setSelectedId = (
    next: number | null | ((cur: number | null) => number | null),
  ) => {
    if (typeof next === "function") {
      setSelectedIds((ids) => {
        const v = next(ids[0] ?? null);
        return v != null ? [v] : [];
      });
      return;
    }
    setSelectedIds(next != null ? [next] : []);
  };
  const [draft, setDraft] = useState<[number, number][]>([]);
  const [newBoardName, setNewBoardName] = useState("");
  const [newBoardKind, setNewBoardKind] = useState<"situation" | "coa" | "overlay">(
    "situation",
  );
  const [deleteBoardOpen, setDeleteBoardOpen] = useState(false);
  const [leftRailCollapsed, setLeftRailCollapsed] = useState(() =>
    readRailCollapsed("gpml.tactical.left.collapsed"),
  );
  const setLeftRailCollapsedPersist = (collapsed: boolean) => {
    setLeftRailCollapsed(collapsed);
    writeRailCollapsed("gpml.tactical.left.collapsed", collapsed);
  };
  const [leftRailWidthPx, setLeftRailWidthPx] = useState(() =>
    readRailWidth("gpml.tactical.left.width"),
  );
  const setLeftRailWidthPersist = (widthPx: number) => {
    const next = clampTacticalRailWidth(widthPx);
    setLeftRailWidthPx(next);
    writeRailWidth("gpml.tactical.left.width", next);
  };
  const [rightRailCollapsed, setRightRailCollapsed] = useState(() =>
    readRailCollapsed("gpml.tactical.right.collapsed"),
  );
  const setRightRailCollapsedPersist = (collapsed: boolean) => {
    setRightRailCollapsed(collapsed);
    writeRailCollapsed("gpml.tactical.right.collapsed", collapsed);
  };
  const [rightRailWidthPx, setRightRailWidthPx] = useState(() =>
    readRailWidth("gpml.tactical.right.width"),
  );
  const setRightRailWidthPersist = (widthPx: number) => {
    const next = clampTacticalRailWidth(widthPx);
    setRightRailWidthPx(next);
    writeRailWidth("gpml.tactical.right.width", next);
  };

  useEffect(() => {
    const onResize = () => {
      setLeftRailWidthPx((current) => clampTacticalRailWidth(current));
      setRightRailWidthPx((current) => clampTacticalRailWidth(current));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [textDraft, setTextDraft] = useState<{ lng: number; lat: number } | null>(null);
  const [textLabel, setTextLabel] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState<TacticalSymbolMeta | null>(null);
  const [presenceUserIds, setPresenceUserIds] = useState<string[]>([]);
  const [collabConnected, setCollabConnected] = useState(false);
  const [mapInstance, setMapInstance] = useState<MapLibreMapType | null>(null);
  const [remoteCursors, setRemoteCursors] = useState<RemoteCursor[]>([]);
  const [remoteDrafts, setRemoteDrafts] = useState<CollabDraft[]>([]);
  /** null = live «сейчас»; иначе индекс события в timeline (0..n-1). */
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const [compareOtherId, setCompareOtherId] = useState<number | null>(null);
  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [newMemberRole, setNewMemberRole] =
    useState<TacticalBoardMemberRole>("viewer");
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [trackToDelete, setTrackToDelete] = useState<number | null>(null);
  const [view3d, setView3d] = useState(false);
  const [losMode, setLosMode] = useState(false);
  const [losObserver, setLosObserver] = useState<{ lng: number; lat: number } | null>(
    null,
  );
  const [losTarget, setLosTarget] = useState<{ lng: number; lat: number } | null>(null);
  const [losResult, setLosResult] = useState<LosResult | null>(null);
  const [observerAglM, setObserverAglM] = useState(2);
  const [targetAglM, setTargetAglM] = useState(2);
  const [terrainOn, setTerrainOn] = useState(false);
  const [activePluginId, setActivePluginId] = useState<string | null>(null);
  const [rangeRingRadiusM, setRangeRingRadiusM] = useState(1000);
  const [rangeRingFeature, setRangeRingFeature] = useState<GeoJSON.Feature<
    GeoJSON.Polygon
  > | null>(null);
  const [regionZMin, setRegionZMin] = useState(10);
  const [regionZMax, setRegionZMax] = useState(13);
  const [regionProgress, setRegionProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [regionDownloading, setRegionDownloading] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState<string | null>(null);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [snapToMvt, setSnapToMvt] = useState(false);
  const [snapToleranceM, setSnapToleranceM] = useState(25);
  const [bufferRadiusM, setBufferRadiusM] = useState(100);
  const [viewshedRadiusM, setViewshedRadiusM] = useState(2000);
  const [viewshedAglM, setViewshedAglM] = useState(2);
  const [viewshedObserver, setViewshedObserver] = useState<{
    lng: number;
    lat: number;
  } | null>(null);
  const [viewshedResult, setViewshedResult] = useState<ViewshedResult | null>(null);
  const [measureMode, setMeasureMode] = useState<MeasureMode>("line");
  const [measureLabel, setMeasureLabel] = useState<string | null>(null);
  const [importPayloads, setImportPayloads] = useState<FeatureCreatePayload[] | null>(
    null,
  );
  const [importSourceLabel, setImportSourceLabel] = useState<string>("файл");
  const [importBusy, setImportBusy] = useState(false);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [cadBooleanMode, setCadBooleanMode] = useState<CadBooleanMode>(null);
  const [bindObjectFromMap, setBindObjectFromMap] = useState(false);
  const bindObjectFromMapRef = useRef(false);
  bindObjectFromMapRef.current = bindObjectFromMap;
  const [activeGroupIdForCreate, setActiveGroupIdForCreate] = useState<number | null>(null);
  const activeGroupIdForCreateRef = useRef<number | null>(null);
  activeGroupIdForCreateRef.current = activeGroupIdForCreate;
  const finishFreehandRef = useRef<() => void>(() => {});
  const pluginCleanupRef = useRef<(() => void) | null>(null);
  const featuresRef = useRef<TacticalFeature[]>([]);
  const snapEnabledRef = useRef(snapEnabled);
  const snapToMvtRef = useRef(snapToMvt);
  const snapToleranceRef = useRef(snapToleranceM);
  const mapInstanceRef = useRef<MapLibreMapType | null>(null);
  const featureHistoryRef = useRef(new TacticalFeatureHistory());
  const historyReplayingRef = useRef(false);
  const [historyTick, setHistoryTick] = useState(0);
  const [historyBusy, setHistoryBusy] = useState(false);
  const boardIdRef = useRef<number | null>(null);
  const isOnlineRef = useRef(true);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  isOnlineRef.current = isOnline;
  const [pendingMutations, setPendingMutations] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  const finishDraftRef = useRef<() => void>(() => {});
  const collabRoomRef = useRef<TacticalCollabRoom | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const flushingRef = useRef(false);
  const historyMode = historyIndex != null;
  const compareMode = compareOtherId != null;
  boardIdRef.current = boardId;
  const canUndo =
    historyTick >= 0 &&
    featureHistoryRef.current.canUndo &&
    !historyBusy &&
    !historyMode &&
    !compareMode &&
    isOnline;
  const canRedo =
    historyTick >= 0 &&
    featureHistoryRef.current.canRedo &&
    !historyBusy &&
    !historyMode &&
    !compareMode &&
    isOnline;
  const bumpFeatureHistory = () => setHistoryTick((n) => n + 1);
  const canManage =
    canManagePerm &&
    !historyMode &&
    !compareMode &&
    !losMode &&
    activePluginId !== "range-ring" &&
    activePluginId !== "viewshed" &&
    activePluginId !== "measure";

  const boardsQuery = useQuery({
    queryKey: ["tactical", "boards"],
    queryFn: () => tacticalApi.listBoards(),
  });

  const symbolsQuery = useQuery({
    queryKey: ["tactical", "meta", "symbols"],
    queryFn: () => tacticalApi.listSymbols(),
  });

  const boardQuery = useQuery({
    queryKey: ["tactical", "board", boardId],
    queryFn: () => tacticalApi.getBoard(boardId!),
    enabled: boardId != null,
  });

  const historyQuery = useQuery({
    queryKey: ["tactical", "history", boardId],
    queryFn: () => tacticalApi.listHistory(boardId!),
    enabled: boardId != null,
  });
  const historyItems = historyQuery.data?.items ?? [];

  const enterHistoryFrame = () => {
    setToolMode("select");
    setDraft([]);
    setTextDraft(null);
    setSelectedId(null);
    setSelectedSymbol(null);
  };

  const historyPlayback = useTacticalHistoryPlayback({
    eventCount: historyItems.length,
    historyIndex,
    setHistoryIndex,
    onEnterHistoryFrame: enterHistoryFrame,
    resetKey: boardId,
  });
  const historyPlaybackRef = useRef(historyPlayback);
  historyPlaybackRef.current = historyPlayback;

  const asOfIso =
    historyIndex != null && historyItems[historyIndex]
      ? historyItems[historyIndex]!.changed_at
      : null;

  const featuresQuery = useQuery({
    queryKey: ["tactical", "features", boardId, asOfIso ?? "live"],
    queryFn: async () => {
      if (asOfIso) {
        setFromCache(false);
        const fc = await tacticalApi.listFeaturesAsOf(boardId!, asOfIso);
        return fc.features ?? [];
      }
      try {
        const fc = await tacticalApi.listFeatures(boardId!);
        const list = fc.features ?? [];
        await putBoardFeaturesCache(boardId!, list);
        setFromCache(false);
        return list;
      } catch (err) {
        const cached = await getBoardFeaturesCache(boardId!);
        if (cached?.features?.length) {
          setFromCache(true);
          return cached.features;
        }
        throw err;
      }
    },
    enabled: boardId != null,
  });

  const compareQuery = useQuery({
    queryKey: ["tactical", "compare", boardId, compareOtherId],
    queryFn: () => tacticalApi.compareBoards(boardId!, compareOtherId!),
    enabled: boardId != null && compareOtherId != null && !historyMode,
  });

  const membersQuery = useQuery({
    queryKey: ["tactical", "members", boardId],
    queryFn: () => tacticalApi.listMembers(boardId!),
    enabled: boardId != null,
  });

  const tracksQuery = useQuery({
    queryKey: ["tactical", "tracks", boardId],
    queryFn: () => tacticalApi.listTracks(boardId!),
    enabled: boardId != null,
  });

  const regionsQuery = useQuery({
    queryKey: ["tactical", "basemap-regions"],
    queryFn: () => listBasemapRegions(),
  });

  const plugins = useMemo(() => listTacticalPlugins(), []);

  snapEnabledRef.current = snapEnabled;
  snapToMvtRef.current = snapToMvt;
  snapToleranceRef.current = snapToleranceM;
  mapInstanceRef.current = mapInstance;

  const features = featuresQuery.data ?? [];
  const mapFeatures = useMemo(() => {
    if (compareMode && compareQuery.data) {
      const diff = compareQuery.data;
      const tagged: TacticalFeature[] = [
        ...diff.only_base.map((f) => tagCompare(f, "only_base")),
        ...diff.only_other.map((f) => tagCompare(f, "only_other")),
        ...diff.changed.map((p) => tagCompare(p.other, "changed")),
      ];
      return withMapIcons(tagged);
    }
    const visible = features.filter((f) => f.properties?.effectivelyVisible !== false);
    return withMapIcons(visible);
  }, [features, compareMode, compareQuery.data]);

  const mapGeoJson = useMemo(
    () => ({ type: "FeatureCollection" as const, features: mapFeatures }),
    [mapFeatures],
  );
  featuresRef.current = features;

  useEffect(() => {
    if (boardId == null && boardsQuery.data && boardsQuery.data.length > 0) {
      setBoardId(boardsQuery.data[0].id);
    }
  }, [boardId, boardsQuery.data]);

  useEffect(() => {
    setHistoryIndex(null);
    setCompareOtherId(null);
    setSelectedId(null);
    setDraft([]);
    setToolMode("select");
    setFromCache(false);
    setNewMemberUserId("");
    setNewMemberRole("viewer");
    setMemberToRemove(null);
    setTrackToDelete(null);
    setLosMode(false);
    setLosObserver(null);
    setLosTarget(null);
    setLosResult(null);
    setActivePluginId(null);
    setRangeRingFeature(null);
    setViewshedObserver(null);
    setViewshedResult(null);
    setMeasureLabel(null);
    setCadBooleanMode(null);
    setBindObjectFromMap(false);
    setActiveGroupIdForCreate(null);
    featureHistoryRef.current.clear();
    bumpFeatureHistory();
  }, [boardId]);

  useEffect(() => {
    pluginCleanupRef.current?.();
    pluginCleanupRef.current = null;
    if (!activePluginId) {
      setRangeRingFeature(null);
      setViewshedObserver(null);
      setViewshedResult(null);
      setMeasureLabel(null);
      return;
    }
    if (activePluginId !== "viewshed") {
      setViewshedObserver(null);
      setViewshedResult(null);
    }
    if (activePluginId !== "measure") {
      setMeasureLabel(null);
    }
    if (activePluginId === "measure") {
      setToolMode("pan");
      setDraft([]);
      setTextDraft(null);
      setSelectedId(null);
    }
    const tool = getTacticalPlugin(activePluginId);
    if (!tool) return;
    const cleanup = tool.activate({
      map: mapInstance,
      boardId,
      toast: (opts) => toast(opts),
      setLosMode: (on) => {
        setLosMode(on);
        if (on) {
          setToolMode("pan");
          setDraft([]);
          setTextDraft(null);
          setSelectedId(null);
          setLosObserver(null);
          setLosTarget(null);
          setLosResult(null);
        } else {
          setLosObserver(null);
          setLosTarget(null);
          setLosResult(null);
        }
      },
    });
    pluginCleanupRef.current = typeof cleanup === "function" ? cleanup : null;
    return () => {
      pluginCleanupRef.current?.();
      pluginCleanupRef.current = null;
    };
  }, [activePluginId, mapInstance, boardId, toast]);

  useEffect(() => {
    if (!mapInstance || activePluginId !== "range-ring") return;
    const onClick = (e: { lngLat: { lng: number; lat: number } }) => {
      setRangeRingFeature(
        circlePolygon(e.lngLat.lng, e.lngLat.lat, Math.max(1, rangeRingRadiusM)),
      );
    };
    mapInstance.on("click", onClick);
    return () => {
      mapInstance.off("click", onClick);
    };
  }, [mapInstance, activePluginId, rangeRingRadiusM]);

  useEffect(() => {
    if (!mapInstance || activePluginId !== "viewshed" || !viewshedObserver) {
      if (activePluginId !== "viewshed") return;
      if (!viewshedObserver) setViewshedResult(null);
      return;
    }
    const result = computeViewshed({
      observer: viewshedObserver,
      radiusM: viewshedRadiusM,
      aglM: viewshedAglM,
      sampleGroundM: (lng, lat) => queryGroundM(mapInstance, lng, lat),
    });
    setViewshedResult(result);
  }, [
    mapInstance,
    activePluginId,
    viewshedObserver,
    viewshedRadiusM,
    viewshedAglM,
    terrainOn,
  ]);

  useEffect(() => {
    if (!mapInstance || activePluginId !== "viewshed") return;
    const onClick = (e: { lngLat: { lng: number; lat: number } }) => {
      const pt = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      setViewshedObserver(pt);
      const result = computeViewshed({
        observer: pt,
        radiusM: viewshedRadiusM,
        aglM: viewshedAglM,
        sampleGroundM: (lng, lat) => queryGroundM(mapInstance, lng, lat),
      });
      setViewshedResult(result);
      if (result.flatEarth) {
        toast({
          title: "Viewshed на плоской земле — задайте VITE_TACTICAL_DEM_TILES_URL",
        });
      }
    };
    mapInstance.on("click", onClick);
    return () => {
      mapInstance.off("click", onClick);
    };
  }, [
    mapInstance,
    activePluginId,
    viewshedRadiusM,
    viewshedAglM,
    toast,
  ]);

  useEffect(() => {
    if (!mapInstance) return;
    const apply = () => setTerrainOn(ensureTacticalTerrain(mapInstance));
    if (mapInstance.isStyleLoaded()) apply();
    else mapInstance.once("load", apply);
    mapInstance.on("styledata", apply);
    return () => {
      mapInstance.off("styledata", apply);
    };
  }, [mapInstance]);

  useEffect(() => {
    if (!mapInstance) return;
    if (view3d) {
      mapInstance.easeTo({ pitch: 55, duration: 400 });
      mapInstance.dragRotate.enable();
      mapInstance.touchZoomRotate.enableRotation();
    } else {
      mapInstance.easeTo({ pitch: 0, bearing: 0, duration: 400 });
      mapInstance.dragRotate.disable();
      mapInstance.touchZoomRotate.disableRotation();
    }
  }, [mapInstance, view3d]);

  useEffect(() => {
    if (!losObserver || !losTarget || !mapInstance) {
      if (!losObserver || !losTarget) setLosResult(null);
      return;
    }
    const result = computeLos(
      { ...losObserver, aglM: observerAglM },
      { ...losTarget, aglM: targetAglM },
      (lng, lat) => queryGroundM(mapInstance, lng, lat),
    );
    setLosResult(result);
  }, [losObserver, losTarget, observerAglM, targetAglM, mapInstance, terrainOn]);

  useEffect(() => {
    if (!mapInstance || !losMode) return;
    const onClick = (e: { lngLat: { lng: number; lat: number } }) => {
      const pt = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      if (!losObserver || (losObserver && losTarget)) {
        setLosObserver(pt);
        setLosTarget(null);
        setLosResult(null);
        return;
      }
      setLosTarget(pt);
    };
    mapInstance.on("click", onClick);
    return () => {
      mapInstance.off("click", onClick);
    };
  }, [mapInstance, losMode, losObserver, losTarget]);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const refreshPending = async (id: number) => {
    const q = await listMutations(id);
    setPendingMutations(q.length);
  };

  useEffect(() => {
    if (boardId == null) {
      setPendingMutations(0);
      return;
    }
    void refreshPending(boardId);
  }, [boardId]);

  useEffect(() => {
    if (!isOnline || boardId == null || historyMode || flushingRef.current) return;
    let cancelled = false;
    void (async () => {
      const pending = await listMutations(boardId);
      if (cancelled || pending.length === 0) return;
      flushingRef.current = true;
      try {
        const result = await flushTacticalQueue(boardId);
        if (cancelled) return;
        setPendingMutations(result.remaining);
        if (result.flushed > 0) {
          void qc.invalidateQueries({ queryKey: ["tactical", "features", boardId] });
          void qc.invalidateQueries({ queryKey: ["tactical", "history", boardId] });
          toast({
            title:
              result.remaining > 0
                ? `Sync: ${result.flushed} ок, осталось ${result.remaining}`
                : `Sync: выгружено ${result.flushed}`,
            variant: result.error ? "destructive" : "default",
          });
        }
        if (result.error) {
          toast({ title: result.error, variant: "destructive" });
        }
      } finally {
        flushingRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnline, boardId, historyMode, qc, toast]);

  useEffect(() => {
    if (boardId == null || !localUserId) {
      setPresenceUserIds([]);
      setCollabConnected(false);
      setRemoteCursors([]);
      setRemoteDrafts([]);
      return;
    }
    const token = localStorage.getItem("access_token");
    if (!token) return;
    let closed = false;
    let pingTimer: number | undefined;
    let reconnectTimer: number | undefined;

    const clearPing = () => {
      if (pingTimer != null) {
        window.clearInterval(pingTimer);
        pingTimer = undefined;
      }
    };

    const sendWs = (type: string, updateB64: string) => {
      const sock = wsRef.current;
      if (sock?.readyState === WebSocket.OPEN) {
        sock.send(JSON.stringify({ type, update: updateB64 }));
      }
    };

    const room = createTacticalCollabRoom({
      userId: localUserId,
      label: localUserLabel,
      onLocalAwarenessEncoded: (updateB64) => {
        sendWs("tactical.yjs.awareness", updateB64);
      },
      onLocalDocEncoded: (updateB64) => {
        sendWs("tactical.yjs.update", updateB64);
      },
      onFeaturesMirrorChanged: (mirrored) => {
        if (closed || historyMode) return;
        qc.setQueryData<TacticalFeature[]>(
          ["tactical", "features", boardId, "live"],
          mirrored,
        );
        void putBoardFeaturesCache(boardId, mirrored);
      },
      onDraftsChanged: () => {
        if (!closed) setRemoteDrafts(room.getRemoteDrafts());
      },
    });
    collabRoomRef.current = room;
    const refreshCursors = () => setRemoteCursors(room.getRemoteCursors());
    room.awareness.on("update", refreshCursors);

    const connect = () => {
      if (closed) return;
      clearPing();
      const ws = new WebSocket(tacticalWsUrl(token, boardId));
      wsRef.current = ws;
      ws.onopen = () => {
        if (!closed) setCollabConnected(true);
        pingTimer = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };
      ws.onclose = () => {
        clearPing();
        setCollabConnected(false);
        if (wsRef.current === ws) wsRef.current = null;
        if (!closed) {
          reconnectTimer = window.setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data as string) as {
            type?: string;
            presence_user_ids?: string[];
            update?: string;
          };
          if (data.type === "tactical.connected" || data.type === "tactical.presence") {
            if (Array.isArray(data.presence_user_ids)) {
              setPresenceUserIds(data.presence_user_ids);
            }
          }
          if (data.type === "tactical.feature.changed") {
            void qc.invalidateQueries({ queryKey: ["tactical", "features", boardId] });
            void qc.invalidateQueries({ queryKey: ["tactical", "history", boardId] });
          }
          if (data.type === "tactical.tracks.changed") {
            void qc.invalidateQueries({ queryKey: ["tactical", "tracks", boardId] });
          }
          if (data.type === "tactical.yjs.awareness" && data.update) {
            room.applyRemoteAwareness(data.update);
            refreshCursors();
          }
          if (data.type === "tactical.yjs.update" && data.update) {
            room.applyRemoteDocUpdate(data.update);
            setRemoteDrafts(room.getRemoteDrafts());
          }
        } catch {
          /* ignore */
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearPing();
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
      room.awareness.off("update", refreshCursors);
      room.destroy();
      collabRoomRef.current = null;
      setCollabConnected(false);
      setPresenceUserIds([]);
      setRemoteCursors([]);
      setRemoteDrafts([]);
    };
  }, [boardId, qc, localUserId, localUserLabel, historyMode]);

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;
    const onMove = (e: { lngLat: { lng: number; lat: number } }) => {
      collabRoomRef.current?.setLocalCursor(e.lngLat.lng, e.lngLat.lat);
    };
    map.on("mousemove", onMove);
    return () => {
      map.off("mousemove", onMove);
    };
  }, [mapInstance]);

  /** Seed Y.Doc features из REST/кэша (silent — без WS fan-out). */
  useEffect(() => {
    if (historyMode || compareMode) return;
    const room = collabRoomRef.current;
    if (!room || boardId == null) return;
    room.seedFeatures(features);
  }, [features, boardId, historyMode, compareMode]);

  /** Live-черновик линии/полигона в Y.Doc drafts. */
  useEffect(() => {
    const room = collabRoomRef.current;
    if (!room) return;
    if ((toolMode === "line" || toolMode === "polygon") && draft.length > 0) {
      room.setLocalDraft({ kind: toolMode, coordinates: draft });
    } else {
      room.setLocalDraft(null);
    }
  }, [draft, toolMode]);

  const invalidateBoard = (id: number) => {
    void qc.invalidateQueries({ queryKey: ["tactical", "features", id] });
    void qc.invalidateQueries({ queryKey: ["tactical", "board", id] });
    void qc.invalidateQueries({ queryKey: ["tactical", "history", id] });
    void qc.invalidateQueries({ queryKey: ["tactical", "layer-groups", id] });
  };

  const layerGroupsQuery = useQuery({
    queryKey: ["tactical", "layer-groups", boardId],
    queryFn: () => tacticalApi.listLayerGroups(boardId as number),
    enabled: boardId != null,
  });
  const layerGroups = layerGroupsQuery.data ?? [];

  const createBoard = useMutation({
    mutationFn: (name: string) => tacticalApi.createBoard(name, newBoardKind),
    onSuccess: (board) => {
      void qc.invalidateQueries({ queryKey: ["tactical", "boards"] });
      setBoardId(board.id);
      setNewBoardName("");
      toast({ title: "Доска создана" });
    },
    onError: () => toast({ title: "Не удалось создать доску", variant: "destructive" }),
  });

  const deleteBoard = useMutation({
    mutationFn: (id: number) => tacticalApi.deleteBoard(id),
    onSuccess: () => {
      setDeleteBoardOpen(false);
      setBoardId(null);
      void qc.invalidateQueries({ queryKey: ["tactical", "boards"] });
      toast({ title: "Доска удалена" });
    },
    onError: () => toast({ title: "Не удалось удалить доску", variant: "destructive" }),
  });

  const invalidateMembers = (id: number) => {
    void qc.invalidateQueries({ queryKey: ["tactical", "members", id] });
  };

  const addMember = useMutation({
    mutationFn: () => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.addMember(boardId, {
        user_id: newMemberUserId.trim(),
        role: newMemberRole,
      });
    },
    onSuccess: () => {
      if (boardId != null) invalidateMembers(boardId);
      setNewMemberUserId("");
      setNewMemberRole("viewer");
      toast({ title: "Участник добавлен" });
    },
    onError: () =>
      toast({ title: "Не удалось добавить участника", variant: "destructive" }),
  });

  const updateMemberRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TacticalBoardMemberRole }) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.updateMember(boardId, userId, { role });
    },
    onSuccess: () => {
      if (boardId != null) invalidateMembers(boardId);
      toast({ title: "Роль обновлена" });
    },
    onError: () =>
      toast({ title: "Не удалось сменить роль", variant: "destructive" }),
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.removeMember(boardId, userId);
    },
    onSuccess: () => {
      setMemberToRemove(null);
      if (boardId != null) invalidateMembers(boardId);
      toast({ title: "Участник удалён" });
    },
    onError: () =>
      toast({ title: "Не удалось удалить участника", variant: "destructive" }),
  });

  const invalidateTracks = (id: number) => {
    void qc.invalidateQueries({ queryKey: ["tactical", "tracks", id] });
  };

  const createDemoTrack = useMutation({
    mutationFn: () => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.createDemoTrack(boardId);
    },
    onSuccess: () => {
      if (boardId != null) invalidateTracks(boardId);
      toast({ title: "Демо-трек создан" });
    },
    onError: () =>
      toast({ title: "Не удалось создать демо-трек", variant: "destructive" }),
  });

  const toggleTrackVisible = useMutation({
    mutationFn: ({ id, is_visible }: { id: number; is_visible: boolean }) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.updateTrack(boardId, id, { is_visible });
    },
    onSuccess: () => {
      if (boardId != null) invalidateTracks(boardId);
    },
    onError: () =>
      toast({ title: "Не удалось сменить видимость", variant: "destructive" }),
  });

  const deleteTrack = useMutation({
    mutationFn: (trackId: number) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.deleteTrack(boardId, trackId);
    },
    onSuccess: () => {
      setTrackToDelete(null);
      if (boardId != null) invalidateTracks(boardId);
      toast({ title: "Трек удалён" });
    },
    onError: () =>
      toast({ title: "Не удалось удалить трек", variant: "destructive" }),
  });

  const createLayerGroup = useMutation({
    mutationFn: (name: string) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.createLayerGroup(boardId, { name });
    },
    onSuccess: () => {
      if (boardId != null) {
        void qc.invalidateQueries({ queryKey: ["tactical", "layer-groups", boardId] });
      }
      toast({ title: "Группа создана" });
    },
    onError: () =>
      toast({ title: "Не удалось создать группу", variant: "destructive" }),
  });

  const updateLayerGroup = useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: number;
      data: Partial<{ name: string; is_locked: boolean; is_visible: boolean }>;
    }) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.updateLayerGroup(boardId, groupId, data);
    },
    onSuccess: () => {
      if (boardId != null) {
        void qc.invalidateQueries({ queryKey: ["tactical", "layer-groups", boardId] });
        invalidateBoard(boardId);
      }
    },
    onError: () =>
      toast({ title: "Не удалось обновить группу", variant: "destructive" }),
  });

  const deleteLayerGroup = useMutation({
    mutationFn: (groupId: number) => {
      if (boardId == null) throw new Error("no board");
      return tacticalApi.deleteLayerGroup(boardId, groupId);
    },
    onSuccess: (_void, groupId) => {
      if (activeGroupIdForCreate === groupId) setActiveGroupIdForCreate(null);
      if (boardId != null) {
        void qc.invalidateQueries({ queryKey: ["tactical", "layer-groups", boardId] });
        invalidateBoard(boardId);
      }
      toast({ title: "Группа удалена" });
    },
    onError: () =>
      toast({ title: "Не удалось удалить группу", variant: "destructive" }),
  });

  const pushCreateHistory = (created: TacticalFeature) => {
    if (historyReplayingRef.current || !isOnlineRef.current) return;
    const snap = asFeatureSnapshot(created);
    if (!snap) return;
    const logicalId = snap.properties.id;
    const history = featureHistoryRef.current;
    history.push({
      undo: async () => {
        const bid = boardIdRef.current;
        if (bid == null) return;
        const id = history.resolveId(logicalId);
        await tacticalApi.deleteFeature(bid, id);
        collabRoomRef.current?.removeFeature(id);
        invalidateBoard(bid);
        setSelectedId((cur) => (cur === id ? null : cur));
      },
      redo: async () => {
        const bid = boardIdRef.current;
        if (bid == null) return;
        const payload = snapshotToCreatePayload(snap);
        if (!payload) return;
        const createdAgain = await tacticalApi.createFeature(bid, payload);
        const newSnap = asFeatureSnapshot(createdAgain);
        if (newSnap) history.remapId(logicalId, newSnap.properties.id);
        if (createdAgain) collabRoomRef.current?.upsertFeature(createdAgain);
        invalidateBoard(bid);
      },
    });
    bumpFeatureHistory();
  };

  const pushDeleteHistory = (before: TacticalFeature) => {
    if (historyReplayingRef.current || !isOnlineRef.current) return;
    const snap = asFeatureSnapshot(before);
    if (!snap) return;
    const logicalId = snap.properties.id;
    const history = featureHistoryRef.current;
    history.push({
      undo: async () => {
        const bid = boardIdRef.current;
        if (bid == null) return;
        const payload = snapshotToCreatePayload(snap);
        if (!payload) return;
        const created = await tacticalApi.createFeature(bid, payload);
        const newSnap = asFeatureSnapshot(created);
        if (newSnap) history.remapId(logicalId, newSnap.properties.id);
        if (created) collabRoomRef.current?.upsertFeature(created);
        invalidateBoard(bid);
      },
      redo: async () => {
        const bid = boardIdRef.current;
        if (bid == null) return;
        const id = history.resolveId(logicalId);
        await tacticalApi.deleteFeature(bid, id);
        collabRoomRef.current?.removeFeature(id);
        invalidateBoard(bid);
        setSelectedId((cur) => (cur === id ? null : cur));
      },
    });
    bumpFeatureHistory();
  };

  const pushPatchHistory = (
    logicalId: number,
    beforePatch: Record<string, unknown>,
    afterPatch: Record<string, unknown>,
  ) => {
    if (historyReplayingRef.current || !isOnlineRef.current) return;
    const history = featureHistoryRef.current;
    history.push({
      undo: async () => {
        const bid = boardIdRef.current;
        if (bid == null) return;
        const id = history.resolveId(logicalId);
        const feat = await tacticalApi.updateFeature(bid, id, beforePatch);
        if (feat) collabRoomRef.current?.upsertFeature(feat);
        invalidateBoard(bid);
      },
      redo: async () => {
        const bid = boardIdRef.current;
        if (bid == null) return;
        const id = history.resolveId(logicalId);
        const feat = await tacticalApi.updateFeature(bid, id, afterPatch);
        if (feat) collabRoomRef.current?.upsertFeature(feat);
        invalidateBoard(bid);
      },
    });
    bumpFeatureHistory();
  };

  const runFeatureHistoryRef = useRef<(action: "undo" | "redo") => Promise<void>>(
    async () => {},
  );
  runFeatureHistoryRef.current = async (action: "undo" | "redo") => {
    if (!canManage || historyBusy || historyReplayingRef.current || !isOnline) return;
    if (action === "undo" && !featureHistoryRef.current.canUndo) return;
    if (action === "redo" && !featureHistoryRef.current.canRedo) return;
    setHistoryBusy(true);
    historyReplayingRef.current = true;
    try {
      const ok =
        action === "undo"
          ? await featureHistoryRef.current.undo()
          : await featureHistoryRef.current.redo();
      if (!ok) return;
      bumpFeatureHistory();
    } catch (error) {
      toast({
        title: action === "undo" ? "Не удалось отменить" : "Не удалось повторить",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      historyReplayingRef.current = false;
      setHistoryBusy(false);
      bumpFeatureHistory();
    }
  };

  const createFeature = useMutation({
    mutationFn: async (raw: Record<string, unknown>) => {
      if (boardId == null) throw new Error("no board");
      const data =
        raw.layer_group_id == null && activeGroupIdForCreateRef.current != null
          ? { ...raw, layer_group_id: activeGroupIdForCreateRef.current }
          : raw;
      if (!isOnline) {
        const tempId = nextTempFeatureId();
        await enqueueMutation({
          boardId,
          op: "create",
          featureId: tempId,
          payload: data,
        });
        const optimistic = buildOptimisticFeature(tempId, data);
        qc.setQueryData<TacticalFeature[]>(
          ["tactical", "features", boardId, "live"],
          (prev) => [...(prev ?? []), optimistic],
        );
        await putBoardFeaturesCache(
          boardId,
          (qc.getQueryData<TacticalFeature[]>(["tactical", "features", boardId, "live"]) ??
            []) as TacticalFeature[],
        );
        await refreshPending(boardId);
        return optimistic;
      }
      return tacticalApi.createFeature(boardId, data);
    },
    onSuccess: (feat) => {
      if (feat) collabRoomRef.current?.upsertFeature(feat);
      if (boardId != null && isOnline) {
        invalidateBoard(boardId);
        if (feat) pushCreateHistory(feat);
      }
      setDraft([]);
      setTextDraft(null);
      setTextLabel("");
      setToolMode((m) => (m === "freehand" ? m : "select"));
      if (!isOnline) toast({ title: "Сохранено локально — sync при сети" });
    },
    onError: () => toast({ title: "Не удалось создать объект", variant: "destructive" }),
  });

  const deleteFeature = useMutation({
    mutationFn: async (id: number) => {
      if (boardId == null) throw new Error("no board");
      const before = asFeatureSnapshot(
        featuresRef.current.find((f) => Number(f.properties?.id) === id),
      );
      if (!isOnline) {
        await enqueueMutation({ boardId, op: "delete", featureId: id });
        qc.setQueryData<TacticalFeature[]>(
          ["tactical", "features", boardId, "live"],
          (prev) => (prev ?? []).filter((f) => Number(f.properties?.id) !== id),
        );
        await putBoardFeaturesCache(
          boardId,
          (qc.getQueryData<TacticalFeature[]>(["tactical", "features", boardId, "live"]) ??
            []) as TacticalFeature[],
        );
        await refreshPending(boardId);
        return { id, before: null as TacticalFeature | null };
      }
      await tacticalApi.deleteFeature(boardId, id);
      return { id, before };
    },
    onSuccess: (result) => {
      const id = result.id;
      collabRoomRef.current?.removeFeature(id);
      setSelectedId(null);
      if (boardId != null && isOnline) {
        invalidateBoard(boardId);
        if (result.before) pushDeleteHistory(result.before);
      }
      if (!isOnline) toast({ title: "Удаление в очереди sync" });
    },
    onError: () => toast({ title: "Не удалось удалить", variant: "destructive" }),
  });

  const updateFeature = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      if (boardId == null) throw new Error("no board");
      const beforeSnap = asFeatureSnapshot(
        featuresRef.current.find((f) => Number(f.properties?.id) === id),
      );
      const beforePatch = beforeSnap ? snapshotToStylePatch(beforeSnap) : null;
      if (!isOnline) {
        await enqueueMutation({ boardId, op: "update", featureId: id, payload: data });
        const nextList = (
          qc.getQueryData<TacticalFeature[]>(["tactical", "features", boardId, "live"]) ?? []
        ).map((f) => {
          if (Number(f.properties?.id) !== id) return f;
          const lng = data.lng as number | undefined;
          const lat = data.lat as number | undefined;
          let next: TacticalFeature = f;
          if (
            f.geometry.type === "Point" &&
            typeof lng === "number" &&
            typeof lat === "number"
          ) {
            next = {
              ...f,
              geometry: { type: "Point" as const, coordinates: [lng, lat] },
            };
          }
          const props = { ...next.properties };
          if (typeof data.label === "string") props.label = data.label;
          if (typeof data.stroke_color === "string") props.strokeColor = data.stroke_color;
          if (typeof data.fill_color === "string") props.fillColor = data.fill_color;
          if (typeof data.fill_opacity === "number") props.fillOpacity = data.fill_opacity;
          return { ...next, properties: props };
        });
        qc.setQueryData<TacticalFeature[]>(
          ["tactical", "features", boardId, "live"],
          nextList,
        );
        await putBoardFeaturesCache(boardId, nextList);
        await refreshPending(boardId);
        const updated = nextList.find((f) => Number(f.properties?.id) === id) ?? null;
        return { feat: updated, logicalId: id, beforePatch: null, afterPatch: data };
      }
      const feat = await tacticalApi.updateFeature(boardId, id, data);
      return { feat, logicalId: id, beforePatch, afterPatch: data };
    },
    onSuccess: (result) => {
      if (result.feat) collabRoomRef.current?.upsertFeature(result.feat);
      if (boardId != null && isOnline) {
        invalidateBoard(boardId);
        if (result.beforePatch) {
          pushPatchHistory(result.logicalId, result.beforePatch, result.afterPatch);
        }
        if ("symbol_kls_id" in (result.afterPatch ?? {})) {
          void qc.invalidateQueries({
            queryKey: ["tactical", "feature-card", boardId, result.logicalId],
          });
        }
      }
    },
  });

  const shapeMode =
    toolMode === "line" || toolMode === "polygon"
      ? toolMode
      : toolMode === "scissors"
        ? "line"
        : null;
  const panMode = toolMode === "pan";
  const textMode = toolMode === "text";
  const freehandMode = toolMode === "freehand";
  const vertexMode = toolMode === "vertex";
  const rotateMode = toolMode === "rotate";
  const lassoMode = toolMode === "lasso";

  const draftGeoJson = useMemo((): GeoJSON.FeatureCollection | null => {
    if (draft.length === 0) return null;
    if (
      (toolMode === "line" || toolMode === "freehand" || toolMode === "scissors") &&
      draft.length >= 1
    ) {
      return {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: { type: "LineString", coordinates: draft },
            properties: {},
          },
        ],
      };
    }
    if (toolMode === "polygon" && draft.length >= 1) {
      const ring = draft.length >= 3 ? [...draft, draft[0]] : draft;
      return {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry:
              draft.length >= 3
                ? { type: "Polygon", coordinates: [ring] }
                : { type: "LineString", coordinates: draft },
            properties: {},
          },
        ],
      };
    }
    return null;
  }, [draft, toolMode]);

  const applySnap = (lng: number, lat: number): { lng: number; lat: number } => {
    if (!snapEnabledRef.current) return { lng, lat };
    const s = snapPointWithOptionalMvt(
      lng,
      lat,
      featuresRef.current,
      snapToleranceRef.current,
      mapInstanceRef.current,
      snapToMvtRef.current,
    );
    return { lng: s.lng, lat: s.lat };
  };

  const runCadBoolean = useMutation({
    mutationFn: async ({
      mode,
      primaryId,
      secondaryId,
    }: {
      mode: "union" | "difference";
      primaryId: number;
      secondaryId: number;
    }) => {
      if (boardId == null) throw new Error("no board");
      if (mode === "union") {
        return tacticalApi.mergeFeatures(boardId, [primaryId, secondaryId]);
      }
      return tacticalApi.differenceFeatures(boardId, [primaryId, secondaryId]);
    },
    onSuccess: (feat) => {
      setCadBooleanMode(null);
      const id = Number(feat.properties?.id ?? feat.id);
      setSelectedId(Number.isFinite(id) ? id : null);
      if (boardId != null) invalidateBoard(boardId);
      toast({ title: "CAD-операция выполнена" });
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: string }).message)
          : "Не удалось выполнить CAD-операцию";
      toast({ title: msg, variant: "destructive" });
    },
  });

  const splitFeature = useMutation({
    mutationFn: async (coordinates: number[][]) => {
      if (boardId == null) throw new Error("no board");
      if (selectedId == null) {
        throw new Error("Выберите линию или полигон для разреза");
      }
      const feat = features.find((f) => Number(f.properties?.id) === selectedId);
      const kind = feat?.properties?.kind;
      if (kind !== "line" && kind !== "polygon") {
        throw new Error("Ножницы доступны только для line или polygon");
      }
      if (feat?.properties?.effectivelyLocked) {
        throw new Error("Объект заблокирован");
      }
      return tacticalApi.splitFeature(boardId, selectedId, coordinates);
    },
    onSuccess: (collection) => {
      const parts = collection.features ?? [];
      const ids = parts
        .map((f) => Number(f.properties?.id ?? f.id))
        .filter((id) => Number.isFinite(id));
      setSelectedId(ids[0] ?? null);
      setDraft([]);
      setToolMode("select");
      if (boardId != null) invalidateBoard(boardId);
      toast({ title: `Разрезано на частей: ${parts.length}` });
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: string }).message)
          : "Разрез не удался";
      toast({ title: msg, variant: "destructive" });
    },
  });

  const finishDraft = () => {
    if (!canManage || boardId == null) return;
    if (toolMode === "scissors") {
      let points = draft;
      if (points.length >= 2) {
        const last = points[points.length - 1]!;
        const prev = points[points.length - 2]!;
        if (last[0] === prev[0] && last[1] === prev[1]) {
          points = points.slice(0, -1);
        }
      }
      if (points.length < 2) {
        toast({ title: "Режущей линии нужны две точки" });
        return;
      }
      if (splitFeature.isPending) return;
      splitFeature.mutate(points);
      return;
    }
    if (toolMode === "line" && draft.length >= 2) {
      createFeature.mutate({
        kind: "line",
        coordinates: draft,
        stroke_color: STROKE,
      });
      return;
    }
    if (toolMode === "polygon" && draft.length >= 3) {
      const check = validatePolygonCoords(draft);
      if (!check.ok) {
        toast({ title: check.reason, variant: "destructive" });
        return;
      }
      createFeature.mutate({
        kind: "polygon",
        coordinates: draft,
        stroke_color: STROKE,
        fill_color: STROKE,
      });
      return;
    }
  };
  finishDraftRef.current = finishDraft;

  const finishFreehand = () => {
    if (!canManage || boardId == null) {
      setDraft([]);
      return;
    }
    if (draft.length < 2) {
      toast({ title: "Слишком короткий штрих" });
      setDraft([]);
      return;
    }
    createFeature.mutate({
      kind: "line",
      coordinates: draft,
      stroke_color: STROKE,
    });
  };
  finishFreehandRef.current = finishFreehand;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase() ?? "";
      const typing =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        Boolean(target?.isContentEditable);

      if (e.key === "Escape") {
        if (historyPlaybackRef.current.phase === "playing") {
          historyPlaybackRef.current.pause();
          return;
        }
        if (bindObjectFromMapRef.current) {
          setBindObjectFromMap(false);
          return;
        }
        if (activePluginId) {
          setActivePluginId(null);
          setLosMode(false);
          setLosObserver(null);
          setLosTarget(null);
          setLosResult(null);
          setRangeRingFeature(null);
          setViewshedObserver(null);
          setViewshedResult(null);
          setMeasureLabel(null);
          setCadBooleanMode(null);
          return;
        }
        if (cadBooleanMode) {
          setCadBooleanMode(null);
          return;
        }
        if (losMode) {
          setLosMode(false);
          setLosObserver(null);
          setLosTarget(null);
          setLosResult(null);
          return;
        }
        if (view3d) {
          setView3d(false);
          return;
        }
        setToolMode("select");
        setDraft([]);
        setTextDraft(null);
        setSelectedId(null);
        setSelectedSymbol(null);
        setCadBooleanMode(null);
        return;
      }

      if (!typing && (e.ctrlKey || e.metaKey)) {
        const k = e.key.toLowerCase();
        if (k === "z" && !e.shiftKey) {
          e.preventDefault();
          void runFeatureHistoryRef.current("undo");
          return;
        }
        if (k === "y" || (k === "z" && e.shiftKey)) {
          e.preventDefault();
          void runFeatureHistoryRef.current("redo");
          return;
        }
      }

      if (
        !typing &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        e.key.length === 1
      ) {
        const k = e.key.toLowerCase();
        const hotkeyTools: Record<string, ToolMode> = {
          v: "select",
          s: "select",
          h: "pan",
          p: "point",
          l: "line",
          g: "polygon",
          t: "text",
          f: "freehand",
          r: "rotate",
          w: "lasso",
        };
        const next = hotkeyTools[k];
        if (next) {
          if (!canManage && next !== "select" && next !== "pan") return;
          if (next === "rotate" && selectedIds.length !== 1) {
            toast({ title: "Выберите одну фичу для поворота" });
            return;
          }
          if (
            next !== "select" &&
            next !== "pan" &&
            (historyPlaybackRef.current.phase === "playing" ||
              historyPlaybackRef.current.phase === "paused" ||
              historyIndex != null)
          ) {
            historyPlaybackRef.current.stop();
          }
          e.preventDefault();
          setActivePluginId(null);
          setLosMode(false);
          setLosObserver(null);
          setLosTarget(null);
          setLosResult(null);
          setRangeRingFeature(null);
          setCadBooleanMode(null);
          setToolMode(next);
          setDraft([]);
          setTextDraft(null);
          if (next !== "point") setSelectedSymbol(null);
          return;
        }
      }

      if (
        e.key === "Enter" &&
        (toolMode === "line" || toolMode === "polygon" || toolMode === "scissors")
      ) {
        finishDraftRef.current();
      }
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedIds.length > 0 &&
        canManage &&
        toolMode !== "vertex" &&
        toolMode !== "scissors"
      ) {
        for (const id of selectedIds) {
          const feat = features.find((f) => Number(f.properties?.id) === id);
          if (feat?.properties?.effectivelyLocked) {
            toast({ title: "Объект заблокирован" });
            continue;
          }
          deleteFeature.mutate(id);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    toolMode,
    selectedIds,
    canManage,
    features,
    deleteFeature,
    toast,
    losMode,
    view3d,
    activePluginId,
    cadBooleanMode,
    historyIndex,
  ]);

  const board = boardQuery.data;
  const boards = boardsQuery.data ?? [];

  return (
    <div className="flex h-screen flex-col bg-background">
      <Header />
      <ModuleMapUiStubProvider setActiveTool={() => {}} enableReferenceLayers>
      <div className="flex min-h-0 flex-1">
        <TacticalSideRail
          side="left"
          title="Тактическая обстановка"
          collapsed={leftRailCollapsed}
          onCollapsedChange={setLeftRailCollapsedPersist}
          widthPx={leftRailWidthPx}
          onWidthChange={setLeftRailWidthPersist}
          headerExtra={
            <SectionHelpTooltip label="Справка: тактическая обстановка">
              <p>
                Слева — доски, слои и объекты. Сверху слева на карте — инструменты (сворачиваются).
                Справа — инспектор свойств. Меню «Доска» — обмен, участники, COA, offline.
              </p>
              <ul className="list-disc space-y-1 pl-4">
                <li>Стрелки на панелях — свернуть; край панели — ширина.</li>
                <li>Список объектов синхронизирован с выбором на карте; Esc снимает режим.</li>
                <li>История и time-lapse — шкала внизу карты.</li>
                <li>Подробности по инструментам — «?» у тулбара карты и у вкладок инспектора.</li>
              </ul>
            </SectionHelpTooltip>
          }
        >
          {canManagePerm && (
            <div className="flex flex-col gap-1 border-b p-2">
              <div className="flex gap-1">
                <Input
                  value={newBoardName}
                  onChange={(e) => setNewBoardName(e.target.value)}
                  placeholder="Имя доски"
                  className="h-8 text-sm"
                />
                <Button
                  size="sm"
                  className="h-8 shrink-0"
                  disabled={!newBoardName.trim() || createBoard.isPending}
                  onClick={() => createBoard.mutate(newBoardName.trim())}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <select
                className="h-8 rounded-md border bg-background px-2 text-xs"
                value={newBoardKind}
                onChange={(e) =>
                  setNewBoardKind(e.target.value as "situation" | "coa" | "overlay")
                }
                aria-label="Тип доски"
              >
                <option value="situation">situation</option>
                <option value="coa">coa</option>
                <option value="overlay">overlay</option>
              </select>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {boardsQuery.isLoading && (
              <p className="px-1 text-xs text-muted-foreground">Загрузка…</p>
            )}
            {boards.map((b: TacticalBoard) => (
              <button
                key={b.id}
                type="button"
                className={cn(
                  "mb-1 w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted",
                  boardId === b.id && "bg-muted font-medium",
                )}
                onClick={() => {
                  setBoardId(b.id);
                  setSelectedId(null);
                  setDraft([]);
                  setToolMode("select");
                  setActiveGroupIdForCreate(null);
                }}
              >
                <span className="block truncate">{b.name}</span>
                <span className="text-[10px] text-muted-foreground">{b.kind}</span>
              </button>
            ))}
            {boardId != null && (
              <div className="mt-2 border-t pt-2 px-1">
                <TacticalBoardMenu disabled={historyMode || compareMode}>
            {boardId != null && (
              <div className="mt-2 space-y-1 border-t pt-2">
                <div className="flex items-center gap-1 px-1">
                  <p className="text-xs font-medium text-muted-foreground">Обмен</p>
                  <SectionHelpTooltip label="Справка: импорт/экспорт доски">
                    <p>
                      Выгрузка и загрузка фич доски. Экспорт — бэкап/перенос во внешние ГИС;
                      импорт добавляет объекты на текущую доску (не заменяет доску).
                    </p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>
                        Форматы: JSON (geopulseml.tactical.board / FeatureCollection), KML/KMZ,
                        Shapefile (ZIP).
                      </li>
                      <li>Экспорт доступен при открытой доске (без manage).</li>
                      <li>
                        Импорт — при праве правки; перед созданием — подтверждение в диалоге.
                      </li>
                      <li>Multi-геометрии разбиваются; неподдерживаемые типы пропускаются.</li>
                    </ul>
                  </SectionHelpTooltip>
                </div>
                <div className="flex gap-1 px-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 flex-1 text-xs"
                        disabled={historyMode || compareMode || featuresQuery.isLoading}
                        title="Экспорт фич"
                      >
                        <Download className="mr-1 h-3.5 w-3.5" />
                        Экспорт
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="z-[230]">
                      {(
                        [
                          ["json", "JSON"],
                          ["kml", "KML"],
                          ["shapefile", "Shapefile (ZIP)"],
                        ] as const
                      ).map(([fmt, label]) => (
                        <DropdownMenuItem
                          key={fmt}
                          onSelect={() => {
                            void exportBoardAs(fmt, board ?? null, features, boardId)
                              .then(({ count }) => {
                                toast({ title: `Экспорт ${label}: ${count} фич` });
                              })
                              .catch((err: unknown) => {
                                toast({
                                  title: "Экспорт",
                                  description:
                                    err instanceof Error ? err.message : "Не удалось выгрузить",
                                  variant: "destructive",
                                });
                              });
                          }}
                        >
                          {label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 flex-1 text-xs"
                    disabled={!canManage || historyMode || compareMode || importBusy}
                    title="Импорт JSON / KML / Shapefile"
                    onClick={() => importFileRef.current?.click()}
                  >
                    <Upload className="mr-1 h-3.5 w-3.5" />
                    Импорт
                  </Button>
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".json,.kml,.kmz,.zip,application/json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/zip"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      void (async () => {
                        try {
                          const prepared = await parseGisImportFile(file);
                          if (prepared.payloads.length === 0) {
                            toast({
                              title: "Нет подходящих фич для импорта",
                              description:
                                prepared.skipped > 0
                                  ? `Пропущено геометрий: ${prepared.skipped}`
                                  : undefined,
                              variant: "destructive",
                            });
                            return;
                          }
                          setImportSourceLabel(prepared.sourceLabel);
                          setImportPayloads(prepared.payloads);
                          if (prepared.skipped > 0) {
                            toast({
                              title: `К импорту ${prepared.payloads.length}, пропущено ${prepared.skipped}`,
                            });
                          }
                        } catch (err) {
                          toast({
                            title:
                              err instanceof Error
                                ? err.message
                                : "Не удалось разобрать файл",
                            variant: "destructive",
                          });
                        }
                      })();
                    }}
                  />
                </div>
              </div>
            )}
            {boardId != null && boards.length > 1 && (
              <div className="mt-2 space-y-1 border-t pt-2">
                <p className="px-1 text-xs font-medium text-muted-foreground">Сравнить с</p>
                <select
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                  value={compareOtherId ?? ""}
                  disabled={historyMode}
                  aria-label="Доска для сравнения COA"
                  onChange={(e) => {
                    const v = e.target.value;
                    historyPlayback.stop();
                    setCompareOtherId(v ? Number(v) : null);
                    setToolMode("select");
                    setDraft([]);
                    setSelectedId(null);
                  }}
                >
                  <option value="">— нет —</option>
                  {boards
                    .filter((b) => b.id !== boardId)
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.kind})
                      </option>
                    ))}
                </select>
                {compareMode && compareQuery.data && (
                  <div className="space-y-0.5 px-1 text-[10px] text-muted-foreground">
                    <p>
                      <span className="text-[#b91c1c]">●</span> только база:{" "}
                      {compareQuery.data.only_base.length}
                    </p>
                    <p>
                      <span className="text-[#15803d]">●</span> только вариант:{" "}
                      {compareQuery.data.only_other.length}
                    </p>
                    <p>
                      <span className="text-[#c2410c]">●</span> изменено:{" "}
                      {compareQuery.data.changed.length}
                    </p>
                    <p>без изменений: {compareQuery.data.unchanged_count}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-full px-1 text-xs"
                      onClick={() => setCompareOtherId(null)}
                    >
                      Сбросить сравнение
                    </Button>
                  </div>
                )}
                {compareMode && compareQuery.isError && (
                  <p className="px-1 text-[10px] text-destructive">Не удалось сравнить</p>
                )}
              </div>
            )}
            {boardId != null && (
              <div className="mt-2 space-y-1 border-t pt-2">
                <div className="flex items-center gap-1 px-1">
                  <p className="text-xs font-medium text-muted-foreground">Участники</p>
                  <SectionHelpTooltip label="Справка: участники доски">
                    <p>
                      Состав доски: роли viewer (чтение), editor (правка фич), owner
                      (состав + правка). Создатель доски — owner.
                    </p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>Список виден всем с доступом к доске.</li>
                      <li>
                        Добавление: введите UUID пользователя и роль, затем «+» (нужен
                        owner / создатель).
                      </li>
                      <li>Смена роли и удаление — у строки участника.</li>
                      <li>Последнего owner удалить или понизить нельзя.</li>
                    </ul>
                  </SectionHelpTooltip>
                </div>
                {membersQuery.isLoading && (
                  <p className="px-1 text-[10px] text-muted-foreground">Загрузка…</p>
                )}
                {(membersQuery.data ?? []).map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-1 rounded-md px-1 py-0.5 text-xs"
                  >
                    <span
                      className="min-w-0 flex-1 truncate font-mono text-[10px]"
                      title={m.user_id}
                    >
                      {m.user_id === localUserId ? `${m.user_id.slice(0, 8)}… (я)` : m.user_id}
                    </span>
                    {canManagePerm ? (
                      <select
                        className="h-7 max-w-[5.5rem] shrink-0 rounded border bg-background px-1 text-[10px]"
                        value={m.role}
                        aria-label={`Роль ${m.user_id}`}
                        disabled={updateMemberRole.isPending}
                        onChange={(e) =>
                          updateMemberRole.mutate({
                            userId: m.user_id,
                            role: e.target.value as TacticalBoardMemberRole,
                          })
                        }
                      >
                        <option value="viewer">viewer</option>
                        <option value="editor">editor</option>
                        <option value="owner">owner</option>
                      </select>
                    ) : (
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {m.role}
                      </span>
                    )}
                    {canManagePerm && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 shrink-0 text-destructive"
                        title="Удалить участника"
                        aria-label={`Удалить ${m.user_id}`}
                        onClick={() => setMemberToRemove(m.user_id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                {!membersQuery.isLoading && (membersQuery.data ?? []).length === 0 && (
                  <p className="px-1 text-[10px] text-muted-foreground">Нет записей состава</p>
                )}
                {canManagePerm && (
                  <div className="flex flex-col gap-1 pt-1">
                    <Input
                      value={newMemberUserId}
                      onChange={(e) => setNewMemberUserId(e.target.value)}
                      placeholder="user_id (UUID)"
                      className="h-8 font-mono text-xs"
                      aria-label="ID пользователя для добавления"
                    />
                    <div className="flex gap-1">
                      <select
                        className="h-8 flex-1 rounded-md border bg-background px-2 text-xs"
                        value={newMemberRole}
                        aria-label="Роль нового участника"
                        onChange={(e) =>
                          setNewMemberRole(e.target.value as TacticalBoardMemberRole)
                        }
                      >
                        <option value="viewer">viewer</option>
                        <option value="editor">editor</option>
                        <option value="owner">owner</option>
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 shrink-0"
                        disabled={!newMemberUserId.trim() || addMember.isPending}
                        onClick={() => addMember.mutate()}
                        aria-label="Добавить участника"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-2 space-y-1 border-t pt-2">
              <div className="flex items-center gap-1 px-1">
                <p className="text-xs font-medium text-muted-foreground">Offline-карта</p>
                <SectionHelpTooltip label="Справка: offline-регионы">
                  <p>
                    Скачивает тайлы текущего viewport basemap в Cache Storage (метаданные в
                    IndexedDB). Помогает работе без сети вместе с кэшем фич.
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    <li>Задайте zMin–zMax (разница ≤ 4).</li>
                    <li>Лимит ≈ {MAX_TILES} тайлов — иначе отказ.</li>
                    <li>
                      Шаблон URL для скачивания — из текущего raster-слоя или OSM. Векторная
                      подложка «Martin (вектор)» в переключателе стилей — при{" "}
                      <code className="text-[10px]">VITE_MARTIN_URL</code> +{" "}
                      <code className="text-[10px]">VITE_MARTIN_TILESET</code> (или{" "}
                      <code className="text-[10px]">VITE_MARTIN_STYLE_URL</code>); см.{" "}
                      <code className="text-[10px]">infra/martin</code>.
                    </li>
                    <li>
                      Профиль слоёв:{" "}
                      <code className="text-[10px]">VITE_MARTIN_LAYER_PROFILE=openmaptiles</code>{" "}
                      для OMT-тайлов;{" "}
                      <code className="text-[10px]">protomaps</code> — для PMTiles схемы
                      Protomaps basemaps v4 (классы дорог/покрытия во встроенном стиле).
                    </li>
                  </ul>
                </SectionHelpTooltip>
              </div>
              <div className="flex gap-1 px-1">
                <Input
                  type="number"
                  className="h-7 w-14"
                  value={regionZMin}
                  aria-label="zMin"
                  onChange={(e) => setRegionZMin(Number(e.target.value) || 0)}
                />
                <span className="self-center text-[10px] text-muted-foreground">…</span>
                <Input
                  type="number"
                  className="h-7 w-14"
                  value={regionZMax}
                  aria-label="zMax"
                  onChange={(e) => setRegionZMax(Number(e.target.value) || 0)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-8 w-full text-xs"
                disabled={regionDownloading || !mapInstance || !isOnline}
                onClick={() => {
                  if (!mapInstance) return;
                  const b = mapInstance.getBounds();
                  const west = b.getWest();
                  const south = b.getSouth();
                  const east = b.getEast();
                  const north = b.getNorth();
                  const zMin = Math.max(0, Math.min(22, regionZMin));
                  const zMax = Math.max(0, Math.min(22, regionZMax));
                  const est = estimateTileCount(west, south, east, north, zMin, zMax);
                  if (est > MAX_TILES) {
                    toast({
                      title: `Слишком много тайлов (${est} > ${MAX_TILES})`,
                      variant: "destructive",
                    });
                    return;
                  }
                  const template =
                    extractRasterTileTemplate(mapInstance) || defaultOsmTemplate();
                  setRegionDownloading(true);
                  setRegionProgress({ done: 0, total: est });
                  void prefetchBasemapRegion({
                    name: `Viewport z${zMin}-${zMax}`,
                    west,
                    south,
                    east,
                    north,
                    zMin,
                    zMax,
                    tileUrlTemplate: template,
                    onProgress: setRegionProgress,
                  })
                    .then(() => {
                      void qc.invalidateQueries({ queryKey: ["tactical", "basemap-regions"] });
                      toast({ title: "Регион скачан" });
                    })
                    .catch((err: unknown) => {
                      toast({
                        title:
                          err instanceof Error ? err.message : "Не удалось скачать регион",
                        variant: "destructive",
                      });
                    })
                    .finally(() => {
                      setRegionDownloading(false);
                      setRegionProgress(null);
                    });
                }}
              >
                {regionDownloading
                  ? `Скачивание ${regionProgress?.done ?? 0}/${regionProgress?.total ?? "…"}`
                  : "Скачать viewport"}
              </Button>
              {(regionsQuery.data ?? []).map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-1 rounded-md px-1 py-0.5 text-[10px]"
                >
                  <span className="min-w-0 flex-1 truncate" title={r.name}>
                    {r.name} · {r.tileCount} т.
                  </span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0 text-destructive"
                    aria-label={`Удалить ${r.name}`}
                    onClick={() => setRegionToDelete(r.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {!regionsQuery.isLoading && (regionsQuery.data ?? []).length === 0 && (
                <p className="px-1 text-[10px] text-muted-foreground">Нет регионов</p>
              )}
            </div>

          {canManagePerm && boardId != null && (
            <div className="border-t p-2">
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={() => setDeleteBoardOpen(true)}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Удалить доску
              </Button>
            </div>
          )}
                </TacticalBoardMenu>
              </div>
            )}
            {boardId != null && (
              <TacticalLayersPanel
                groups={layerGroups}
                canManage={canManage}
                selectedFeatureId={selectedId}
                selectedFeatureGroupId={
                  selectedId != null
                    ? features.find((f) => Number(f.properties?.id) === selectedId)?.properties
                        ?.layerGroupId
                    : null
                }
                activeGroupIdForCreate={activeGroupIdForCreate}
                onActiveGroupForCreate={setActiveGroupIdForCreate}
                onCreate={(name) => createLayerGroup.mutate(name)}
                onRename={(groupId, name) =>
                  updateLayerGroup.mutate({ groupId, data: { name } })
                }
                onToggleVisible={(groupId, visible) =>
                  updateLayerGroup.mutate({ groupId, data: { is_visible: visible } })
                }
                onToggleLock={(groupId, locked) =>
                  updateLayerGroup.mutate({ groupId, data: { is_locked: locked } })
                }
                onDelete={(groupId) => deleteLayerGroup.mutate(groupId)}
                onAssignSelected={(groupId) => {
                  if (selectedId == null) return;
                  updateFeature.mutate({
                    id: selectedId,
                    data:
                      groupId == null
                        ? { clear_layer_group: true }
                        : { layer_group_id: groupId },
                  });
                }}
                busy={
                  createLayerGroup.isPending ||
                  updateLayerGroup.isPending ||
                  deleteLayerGroup.isPending ||
                  updateFeature.isPending
                }
              />
            )}
            {boardId != null && (
              <TacticalFeatureList
                features={features}
                groups={layerGroups}
                selectedIds={selectedIds}
                onSelect={(id) => {
                  setSelectedIds([id]);
                  setToolMode("select");
                  setDraft([]);
                  setTextDraft(null);
                }}
              />
            )}
            {!boardsQuery.isLoading && boards.length === 0 && (
              <p className="px-1 text-xs text-muted-foreground">Нет досок. Создайте первую.</p>
            )}
          </div>
          {canManagePerm && (
            <div className="max-h-56 border-t p-2">
              <p className="mb-1 text-xs font-medium text-muted-foreground">MIL-символы</p>
              <div className="grid max-h-44 grid-cols-1 gap-1 overflow-y-auto">
                <button
                  type="button"
                  className={cn(
                    "rounded px-2 py-1 text-left text-xs hover:bg-muted",
                    selectedSymbol == null && toolMode === "point" && "bg-muted font-medium",
                  )}
                  onClick={() => {
                    setSelectedSymbol(null);
                    setToolMode("point");
                  }}
                >
                  Точка без SIDC
                </button>
                {(symbolsQuery.data ?? []).map((sym) => (
                  <button
                    key={sym.kls_id}
                    type="button"
                    title={sym.sidc}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-muted",
                      selectedSymbol?.kls_id === sym.kls_id && "bg-muted font-medium",
                    )}
                    onClick={() => {
                      setSelectedSymbol(sym);
                      setToolMode("point");
                      setDraft([]);
                      setTextDraft(null);
                    }}
                  >
                    <img
                      src={milSidcToDataUrl(sym.sidc, 22)}
                      alt=""
                      className="h-5 w-5 shrink-0"
                    />
                    <span className="truncate">{sym.label}</span>
                  </button>
                ))}
                {symbolsQuery.isLoading && (
                  <p className="px-1 text-xs text-muted-foreground">Загрузка каталога…</p>
                )}
              </div>
            </div>
          )}
        </TacticalSideRail>

        <div className="relative min-h-0 min-w-0 flex-1">
          {boardId == null ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Выберите или создайте доску
            </div>
          ) : (
            <>
              <TacticalMapToolbar
                toolMode={toolMode}
                canManage={canManage}
                cadBooleanMode={cadBooleanMode}
                snapEnabled={snapEnabled}
                snapToleranceM={snapToleranceM}
                snapToMvt={snapToMvt}
                view3d={view3d}
                losActive={losMode || activePluginId === "los"}
                viewshedActive={activePluginId === "viewshed"}
                measureActive={activePluginId === "measure"}
                canUndo={canUndo}
                canRedo={canRedo}
                selectedCount={selectedIds.length}
                collabConnected={collabConnected}
                presenceCount={presenceUserIds.length}
                isOnline={isOnline}
                pendingMutations={pendingMutations}
                fromCache={fromCache}
                unionDisabled={
                  selectedId == null ||
                  features.find((f) => Number(f.properties?.id) === selectedId)?.properties
                    ?.kind !== "polygon" ||
                  runCadBoolean.isPending
                }
                differenceDisabled={
                  selectedId == null ||
                  features.find((f) => Number(f.properties?.id) === selectedId)?.properties
                    ?.kind !== "polygon" ||
                  runCadBoolean.isPending
                }
                onToolMode={(mode) => {
                  setActivePluginId(null);
                  setLosMode(false);
                  setLosObserver(null);
                  setLosTarget(null);
                  setLosResult(null);
                  setRangeRingFeature(null);
                  setCadBooleanMode(null);
                  if (mode === "vertex" || mode === "scissors") {
                    const feat =
                      selectedId != null
                        ? features.find((f) => Number(f.properties?.id) === selectedId)
                        : null;
                    const kind = feat?.properties?.kind;
                    if (kind !== "line" && kind !== "polygon") {
                      toast({
                        title:
                          mode === "scissors"
                            ? "Выберите линию или полигон для разреза"
                            : "Выберите линию или полигон для правки вершин",
                      });
                      return;
                    }
                    if (feat?.properties?.effectivelyLocked) {
                      toast({ title: "Объект заблокирован" });
                      return;
                    }
                  }
                  if (mode === "rotate") {
                    if (selectedIds.length !== 1) {
                      toast({ title: "Выберите одну фичу для поворота" });
                      return;
                    }
                    const feat = features.find(
                      (f) => Number(f.properties?.id) === selectedIds[0],
                    );
                    if (feat?.properties?.effectivelyLocked) {
                      toast({ title: "Объект заблокирован" });
                      return;
                    }
                  }
                  setToolMode(mode);
                  setDraft([]);
                  setTextDraft(null);
                  if (mode !== "point") setSelectedSymbol(null);
                }}
                onToggleUnion={() => {
                  setToolMode("select");
                  setActivePluginId(null);
                  setCadBooleanMode((m) => (m === "union" ? null : "union"));
                  toast({ title: "Кликните второй полигон для объединения" });
                }}
                onToggleDifference={() => {
                  setToolMode("select");
                  setActivePluginId(null);
                  setCadBooleanMode((m) => (m === "difference" ? null : "difference"));
                  toast({ title: "Кликните полигон-вычитаемое" });
                }}
                onToggleSnap={() => setSnapEnabled((v) => !v)}
                onToggle3d={() => setView3d((v) => !v)}
                onToggleLos={() => {
                  setActivePluginId((cur) => (cur === "los" ? null : "los"));
                }}
                onToggleViewshed={() => {
                  setActivePluginId((cur) => (cur === "viewshed" ? null : "viewshed"));
                }}
                onToggleMeasure={() => {
                  setActivePluginId((cur) => (cur === "measure" ? null : "measure"));
                }}
                onUndo={() => void runFeatureHistoryRef.current("undo")}
                onRedo={() => void runFeatureHistoryRef.current("redo")}
                onDeleteSelected={() => {
                  for (const id of selectedIds) {
                    const feat = features.find((f) => Number(f.properties?.id) === id);
                    if (feat?.properties?.effectivelyLocked) {
                      toast({ title: "Объект заблокирован" });
                      continue;
                    }
                    deleteFeature.mutate(id);
                  }
                }}
              />

              {losMode && (
                <div className="absolute right-3 top-14 z-[220] w-56 space-y-2 rounded-md border bg-background/95 p-2 text-xs shadow">
                  <div className="flex items-center gap-1">
                    <p className="font-medium">Прямая видимость</p>
                    <SectionHelpTooltip label="Справка: LOS">
                      <p>
                        Два клика по карте: наблюдатель, затем цель. Зелёная линия —
                        видно, красная — рельеф перекрывает луч.
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        <li>Высоты AGL — над землёй (м) для антенн/наблюдателя.</li>
                        <li>
                          Рельеф:{" "}
                          {terrainOn || tacticalDemTilesUrl()
                            ? "тайлы DEM подключены (или заданы в env)."
                            : "не задан — расчёт на плоской земле (z=0)."}
                        </li>
                        <li>Esc — сброс инструмента LOS.</li>
                      </ul>
                    </SectionHelpTooltip>
                  </div>
                  <label className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Набл. AGL, м</span>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      className="h-7 w-20"
                      value={observerAglM}
                      onChange={(e) => setObserverAglM(Number(e.target.value) || 0)}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Цель AGL, м</span>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      className="h-7 w-20"
                      value={targetAglM}
                      onChange={(e) => setTargetAglM(Number(e.target.value) || 0)}
                    />
                  </label>
                  <p className="text-[10px] text-muted-foreground">
                    {!losObserver
                      ? "Кликните точку наблюдателя"
                      : !losTarget
                        ? "Кликните точку цели"
                        : losResult
                          ? losResult.visible
                            ? `Видимость есть${losResult.usedTerrain ? " (terrain)" : " (плоскость)"}`
                            : `Перекрыто${losResult.usedTerrain ? " (terrain)" : ""}`
                          : "Расчёт…"}
                  </p>
                  {(losObserver || losTarget) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-full"
                      onClick={() => {
                        setLosObserver(null);
                        setLosTarget(null);
                        setLosResult(null);
                      }}
                    >
                      Сбросить точки
                    </Button>
                  )}
                </div>
              )}

              {textDraft && (
                <div className="absolute left-1/2 top-14 z-[220] flex -translate-x-1/2 gap-1 rounded-md border bg-background p-2 shadow">
                  <Input
                    autoFocus
                    value={textLabel}
                    onChange={(e) => setTextLabel(e.target.value)}
                    placeholder="Подпись"
                    className="h-8 w-48"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && textLabel.trim() && canManage && boardId != null) {
                        createFeature.mutate({
                          kind: "text",
                          lng: textDraft.lng,
                          lat: textDraft.lat,
                          label: textLabel.trim(),
                          stroke_color: STROKE,
                        });
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={!textLabel.trim() || createFeature.isPending}
                    onClick={() => {
                      if (!textLabel.trim() || boardId == null) return;
                      createFeature.mutate({
                        kind: "text",
                        lng: textDraft.lng,
                        lat: textDraft.lat,
                        label: textLabel.trim(),
                        stroke_color: STROKE,
                      });
                    }}
                  >
                    OK
                  </Button>
                </div>
              )}

              <Suspense
                  fallback={
                    <div className="p-4 text-sm text-muted-foreground">Загрузка карты…</div>
                  }
                >
                  <MapLibreMap
                    selectedObject={null}
                    geometries={[]}
                    activeTool={activePluginId === "measure" ? "measure" : "select"}
                    measureMode={measureMode}
                    onMeasureDistanceChange={(_m, label) => setMeasureLabel(label)}
                    onMeasurePoint={(pt) => {
                      if (pt) setMeasureLabel(pt.text);
                    }}
                    onMeasureExit={() => {
                      setActivePluginId((cur) => (cur === "measure" ? null : cur));
                      setMeasureLabel(null);
                    }}
                    isDrawing={false}
                    onDrawingComplete={() => {}}
                    useMvt
                    catalogUnderlayEnabled={bindObjectFromMap || (snapEnabled && snapToMvt)}
                    drawingSessionEnabled={false}
                    cadastralLayersVisible={false}
                    telegramIncidentsVisible={false}
                    workItemsVisible={false}
                    showBaseLayerSwitcher
                    baseLayerSwitcherVariant="style-card"
                    symbolGeoJson={mapGeoJson}
                    selectedSymbolId={selectedId}
                    selectedSymbolIds={selectedIds.length > 0 ? selectedIds : null}
                    symbolPlaceMode={toolMode === "point"}
                    symbolPanMode={panMode}
                    symbolTextMode={textMode}
                    symbolShapeMode={shapeMode}
                    symbolFreehandMode={freehandMode}
                    symbolVertexMode={vertexMode}
                    symbolLassoMode={lassoMode}
                    symbolRotateMode={rotateMode}
                    symbolBindObjectFromMapMode={bindObjectFromMap}
                    symbolDraft={draftGeoJson}
                    onMapInstance={setMapInstance}
                    onObjectSelect={(objectId: number) => {
                      if (!canManage || !bindObjectFromMapRef.current || selectedId == null) {
                        return;
                      }
                      updateFeature.mutate({
                        id: selectedId,
                        data: { object_id: objectId },
                      });
                      setBindObjectFromMap(false);
                      toast({ title: `Привязано к объекту #${objectId}` });
                    }}
                    onSymbolFreehandPoint={(lng: number, lat: number) => {
                      if (!canManage) return;
                      const p = applySnap(lng, lat);
                      setDraft((pts) => [...pts, [p.lng, p.lat]]);
                    }}
                    onSymbolFreehandFinish={() => finishFreehandRef.current()}
                    onSymbolGeometryChange={(id, geometry) => {
                      if (!canManage || boardId == null) return;
                      const feat = features.find((f) => Number(f.properties?.id) === id);
                      if (feat?.properties?.effectivelyLocked) {
                        toast({ title: "Объект заблокирован" });
                        return;
                      }
                      const kind = feat?.properties?.kind;
                      if (kind !== "line" && kind !== "polygon") return;
                      let coordinates: [number, number][];
                      if (geometry.type === "Polygon") {
                        const ring = geometry.coordinates[0] ?? [];
                        coordinates = ring.map((c) => [Number(c[0]), Number(c[1])]);
                        if (
                          coordinates.length >= 2 &&
                          coordinates[0]![0] === coordinates[coordinates.length - 1]![0] &&
                          coordinates[0]![1] === coordinates[coordinates.length - 1]![1]
                        ) {
                          coordinates = coordinates.slice(0, -1);
                        }
                        const check = validatePolygonCoords(coordinates);
                        if (!check.ok) {
                          toast({ title: check.reason, variant: "destructive" });
                          if (boardId != null) invalidateBoard(boardId);
                          return;
                        }
                      } else {
                        coordinates = geometry.coordinates.map((c) => [
                          Number(c[0]),
                          Number(c[1]),
                        ]);
                        if (coordinates.length < 2) {
                          toast({
                            title: "У линии должно остаться не меньше двух вершин",
                            variant: "destructive",
                          });
                          if (boardId != null) invalidateBoard(boardId);
                          return;
                        }
                      }
                      updateFeature.mutate({ id, data: { coordinates } });
                    }}
                    onSymbolPlace={(lng: number, lat: number) => {
                      if (!canManage || boardId == null) return;
                      const p = applySnap(lng, lat);
                      createFeature.mutate({
                        kind: "point",
                        lng: p.lng,
                        lat: p.lat,
                        stroke_color: STROKE,
                        fill_color: STROKE,
                        ...(selectedSymbol
                          ? { symbol_kls_id: selectedSymbol.kls_id, size_px: 40 }
                          : {}),
                      });
                    }}
                    onSymbolTextPlace={(lng: number, lat: number) => {
                      if (!canManage) return;
                      const p = applySnap(lng, lat);
                      setTextDraft({ lng: p.lng, lat: p.lat });
                      setTextLabel("");
                    }}
                    onSymbolVertex={(lng: number, lat: number) => {
                      if (!canManage) return;
                      const p = applySnap(lng, lat);
                      setDraft((pts) => [...pts, [p.lng, p.lat]]);
                    }}
                    onSymbolFinish={() => finishDraftRef.current()}
                    onSymbolSelect={(id: number) => {
                      if (
                        cadBooleanMode &&
                        canManage &&
                        selectedId != null &&
                        id !== selectedId &&
                        !runCadBoolean.isPending
                      ) {
                        const primary = features.find(
                          (f) => Number(f.properties?.id) === selectedId,
                        );
                        const secondary = features.find(
                          (f) => Number(f.properties?.id) === id,
                        );
                        if (
                          primary?.properties?.kind !== "polygon" ||
                          secondary?.properties?.kind !== "polygon"
                        ) {
                          toast({
                            title: "Нужны две polygon-фичи",
                            variant: "destructive",
                          });
                          return;
                        }
                        if (
                          primary.properties?.effectivelyLocked ||
                          secondary.properties?.effectivelyLocked
                        ) {
                          toast({ title: "Объект заблокирован", variant: "destructive" });
                          return;
                        }
                        runCadBoolean.mutate({
                          mode: cadBooleanMode,
                          primaryId: selectedId,
                          secondaryId: id,
                        });
                        return;
                      }
                      setToolMode("select");
                      setDraft([]);
                      setTextDraft(null);
                      setCadBooleanMode(null);
                      setSelectedId((cur) => (cur === id ? null : id));
                    }}
                    onSymbolLassoComplete={(ring) => {
                      const ids = signsInsideLasso(features, ring);
                      setSelectedIds(ids);
                      setToolMode("select");
                      if (ids.length === 0) {
                        toast({ title: "В области нет объектов" });
                      } else if (ids.length > 1) {
                        toast({ title: `Выбрано: ${ids.length}` });
                      }
                    }}
                    onSymbolRotateCommit={(id, payload) => {
                      if (!canManage || boardId == null) return;
                      const feat = features.find((f) => Number(f.properties?.id) === id);
                      if (feat?.properties?.effectivelyLocked) {
                        toast({ title: "Объект заблокирован" });
                        return;
                      }
                      if (payload.rotationDeg != null) {
                        updateFeature.mutate({
                          id,
                          data: { rotation_deg: payload.rotationDeg },
                        });
                        return;
                      }
                      if (!payload.geometry) return;
                      if (payload.geometry.type === "LineString") {
                        updateFeature.mutate({
                          id,
                          data: {
                            coordinates: payload.geometry.coordinates.map(
                              (c) => [Number(c[0]), Number(c[1])] as [number, number],
                            ),
                          },
                        });
                        return;
                      }
                      const ring = payload.geometry.coordinates[0] ?? [];
                      let coordinates = ring.map(
                        (c) => [Number(c[0]), Number(c[1])] as [number, number],
                      );
                      if (
                        coordinates.length >= 2 &&
                        coordinates[0]![0] === coordinates[coordinates.length - 1]![0] &&
                        coordinates[0]![1] === coordinates[coordinates.length - 1]![1]
                      ) {
                        coordinates = coordinates.slice(0, -1);
                      }
                      updateFeature.mutate({ id, data: { coordinates } });
                    }}
                    onSymbolDrag={(id: number, lng: number, lat: number) => {
                      if (!canManage || boardId == null) return;
                      const feat = features.find((f) => Number(f.properties?.id) === id);
                      if (feat?.properties?.effectivelyLocked) {
                        toast({ title: "Объект заблокирован" });
                        return;
                      }
                      updateFeature.mutate({ id, data: { lng, lat } });
                    }}
                  />
                  <TacticalRemoteCursors map={mapInstance} cursors={remoteCursors} />
                  <TacticalRemoteDrafts map={mapInstance} drafts={remoteDrafts} />
                  <TacticalTracksOverlay
                    map={mapInstance}
                    tracks={tracksQuery.data ?? []}
                  />
                  <TacticalLosOverlay
                    map={mapInstance}
                    draft={{ observer: losObserver, target: losTarget }}
                    result={losResult}
                  />
                  <TacticalViewshedOverlay map={mapInstance} result={viewshedResult} />
                  <TacticalRangeRingOverlay
                    map={mapInstance}
                    feature={rangeRingFeature}
                  />
                </Suspense>

              <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[220] flex justify-center px-3">
                <div className="pointer-events-auto flex w-full max-w-xl flex-col gap-1 rounded-md border bg-background/95 p-2 shadow">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex min-w-0 items-center gap-1">
                      <span className="truncate">
                        {historyPlayback.phase === "playing"
                          ? "Time-lapse…"
                          : historyMode
                            ? `Снимок: ${
                                asOfIso
                                  ? new Date(asOfIso).toLocaleString("ru-RU")
                                  : "—"
                              } · ${historyItems[historyIndex!]?.action ?? ""}`
                            : "Сейчас (live)"}
                      </span>
                      <SectionHelpTooltip label="Справка: история и time-lapse">
                        <p>
                          Ручной as-of — ползунок по событиям журнала; time-lapse — автопрокрутка
                          тех же снимков (не heatmap и не интерполяция геометрий).
                        </p>
                        <ul className="list-disc space-y-1 pl-4">
                          <li>Play / Pause / Stop («Сейчас») — управление проигрыванием.</li>
                          <li>Скорость — задержка между кадрами-событиями.</li>
                          <li>Во время Play правка недоступна; Esc — пауза на кадре.</li>
                          <li>Сдвиг ползунка во время Play ставит паузу на выбранном кадре.</li>
                        </ul>
                      </SectionHelpTooltip>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {historyPlayback.phase === "playing" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-7 px-2"
                          title="Пауза"
                          aria-label="Пауза time-lapse"
                          onClick={() => historyPlayback.pause()}
                        >
                          <Pause className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-7 px-2"
                          title="Проиграть историю"
                          aria-label="Проиграть историю"
                          disabled={!historyPlayback.canPlay || compareMode}
                          onClick={() => {
                            setCompareOtherId(null);
                            historyPlayback.play();
                          }}
                        >
                          <Play className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant={historyMode ? "default" : "ghost"}
                        className="h-7 gap-1 px-2 text-xs"
                        disabled={!historyMode && historyPlayback.phase === "idle"}
                        title="Сейчас (live)"
                        aria-label="Стоп и сейчас"
                        onClick={() => {
                          historyPlayback.stop();
                          setToolMode("select");
                          setDraft([]);
                          setTextDraft(null);
                        }}
                      >
                        <Square className="h-3 w-3" />
                        Сейчас
                      </Button>
                      <select
                        className="h-7 rounded-md border bg-background px-1 text-[10px]"
                        value={historyPlayback.delayMs}
                        aria-label="Скорость time-lapse"
                        title="Задержка между кадрами"
                        onChange={(e) =>
                          historyPlayback.setDelayMs(
                            Number(e.target.value) as HistoryPlaybackDelayMs,
                          )
                        }
                      >
                        {historyPlayback.delays.map((ms) => (
                          <option key={ms} value={ms}>
                            {ms} мс
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={Math.max(historyItems.length, 1)}
                    step={1}
                    value={
                      historyIndex == null
                        ? Math.max(historyItems.length, 1)
                        : historyIndex
                    }
                    disabled={historyItems.length === 0}
                    className="w-full accent-primary"
                    title="История доски"
                    aria-label="Шкала истории доски"
                    onChange={(e) => {
                      const maxLive = Math.max(historyItems.length, 1);
                      const v = Number(e.target.value);
                      if (v >= maxLive || historyItems.length === 0) {
                        historyPlayback.scrub(null);
                        return;
                      }
                      historyPlayback.scrub(v);
                    }}
                  />
                  {historyItems.length === 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      История появится после создания или правки объектов.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <TacticalRightInspector
          collapsed={rightRailCollapsed}
          onCollapsedChange={setRightRailCollapsedPersist}
          widthPx={rightRailWidthPx}
          onWidthChange={setRightRailWidthPersist}
          hasSelection={selectedId != null && selectedIds.length === 1}
          properties={
            <>
              <TacticalFeatureStyleForm
                feature={
                  selectedId == null
                    ? null
                    : features.find((f) => Number(f.properties?.id) === selectedId) ?? null
                }
                canManage={canManage}
                busy={updateFeature.isPending}
                onSave={(patch) => {
                  if (selectedId == null) return;
                  updateFeature.mutate({ id: selectedId, data: patch });
                }}
              />
              {boardId != null && selectedId != null && selectedIds.length === 1 && (
                <TacticalObjectBindPanel
                  objectId={
                    features.find((f) => Number(f.properties?.id) === selectedId)?.properties
                      ?.objectId ?? null
                  }
                  canManage={canManage}
                  bindFromMap={bindObjectFromMap}
                  busy={updateFeature.isPending}
                  onBindFromMapChange={(active) => {
                    if (active) {
                      setToolMode("select");
                      setDraft([]);
                      setTextDraft(null);
                      setCadBooleanMode(null);
                      setActivePluginId(null);
                    }
                    setBindObjectFromMap(active);
                  }}
                  onChangeObjectId={(objectId) => {
                    if (selectedId == null) return;
                    updateFeature.mutate({
                      id: selectedId,
                      data:
                        objectId == null
                          ? { clear_object: true }
                          : { object_id: objectId },
                    });
                  }}
                />
              )}
            </>
          }
          card={
            boardId != null && selectedId != null && selectedIds.length === 1 ? (
              <TacticalFeatureCardPanel
                boardId={boardId}
                featureId={selectedId}
                canManage={canManage}
                symbolKlsId={
                  (features.find((f) => Number(f.properties?.id) === selectedId)?.properties
                    ?.symbolKlsId as number | null | undefined) ?? null
                }
              />
            ) : null
          }
          analysis={
            <>
            {boardId != null && (
              <div className="mt-2 space-y-1 border-t pt-2">
                <div className="flex items-center gap-1 px-1">
                  <p className="text-xs font-medium text-muted-foreground">Треки</p>
                  <SectionHelpTooltip label="Справка: треки">
                    <p>
                      Треки сенсоров/платформ на доске: линия пути и текущая позиция (head).
                      Отдельно от CAD-фич и сравнения COA.
                    </p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>«Демо-трек» — короткий тестовый маршрут у центра доски.</li>
                      <li>Глаз — показать/скрыть на карте без удаления.</li>
                      <li>Удаление — только при праве manage / editor|owner на доске.</li>
                    </ul>
                  </SectionHelpTooltip>
                </div>
                {tracksQuery.isLoading && (
                  <p className="px-1 text-[10px] text-muted-foreground">Загрузка…</p>
                )}
                {(tracksQuery.data ?? []).map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-1 rounded-md px-1 py-0.5 text-xs"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: t.color }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate" title={t.name}>
                      {t.name}
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({t.point_count})
                      </span>
                    </span>
                    {canManagePerm && (
                      <>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 shrink-0"
                          title={t.is_visible ? "Скрыть" : "Показать"}
                          aria-label={
                            t.is_visible ? `Скрыть ${t.name}` : `Показать ${t.name}`
                          }
                          onClick={() =>
                            toggleTrackVisible.mutate({
                              id: t.id,
                              is_visible: !t.is_visible,
                            })
                          }
                        >
                          {t.is_visible ? (
                            <Eye className="h-3.5 w-3.5" />
                          ) : (
                            <EyeOff className="h-3.5 w-3.5" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 shrink-0 text-destructive"
                          title="Удалить трек"
                          aria-label={`Удалить ${t.name}`}
                          onClick={() => setTrackToDelete(t.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
                {!tracksQuery.isLoading && (tracksQuery.data ?? []).length === 0 && (
                  <p className="px-1 text-[10px] text-muted-foreground">Нет треков</p>
                )}
                {canManagePerm && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 w-full text-xs"
                    disabled={createDemoTrack.isPending}
                    onClick={() => createDemoTrack.mutate()}
                  >
                    Демо-трек
                  </Button>
                )}
              </div>
            )}

            <div className="mt-2 space-y-1 border-t pt-2">
              <div className="flex items-center gap-1 px-1">
                <p className="text-xs font-medium text-muted-foreground">Плагины</p>
                <SectionHelpTooltip label="Справка: плагины">
                  <p>
                    Реестр инструментов workspace: один активный плагин. Новые инструменты
                    регистрируются в коде без раздувания панели CAD.
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    <li>LOS — тот же режим, что кнопка на карте.</li>
                    <li>Viewshed — клик наблюдателя, зона видимости в радиусе.</li>
                    <li>Измерение — длина, площадь, круг, координаты; азимут сегмента.</li>
                    <li>Кольцо дальности — клик + радиус (м), только overlay.</li>
                    <li>Буфер — вокруг выбранной фичи, создаёт polygon на доске.</li>
                    <li>Esc — снять активный плагин.</li>
                  </ul>
                </SectionHelpTooltip>
              </div>
              {plugins.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={cn(
                    "mb-0.5 w-full rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted",
                    activePluginId === p.id && "bg-muted font-medium",
                  )}
                  title={p.description}
                  onClick={() =>
                    setActivePluginId((cur) => (cur === p.id ? null : p.id))
                  }
                >
                  {p.label}
                </button>
              ))}
              {activePluginId === "range-ring" && (
                <label className="flex items-center justify-between gap-2 px-1 pt-1 text-[10px]">
                  <span className="text-muted-foreground">Радиус, м</span>
                  <Input
                    type="number"
                    min={1}
                    className="h-7 w-24"
                    value={rangeRingRadiusM}
                    onChange={(e) => setRangeRingRadiusM(Number(e.target.value) || 1)}
                  />
                </label>
              )}
              {activePluginId === "viewshed" && (
                <div className="space-y-1 px-1 pt-1">
                  <div className="flex items-center gap-1">
                    <p className="text-[10px] font-medium">Viewshed</p>
                    <SectionHelpTooltip label="Справка: viewshed">
                      <p>
                        Зона прямой видимости вокруг точки: клик — наблюдатель. Зелёный
                        полигон по рельефу; серый круг — без DEM (плоская земля).
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        <li>Радиус и AGL задаются в панели.</li>
                        <li>
                          Рельеф:{" "}
                          {terrainOn || tacticalDemTilesUrl()
                            ? "тайлы DEM подключены."
                            : "не задан — задайте VITE_TACTICAL_DEM_TILES_URL."}
                        </li>
                        <li>Esc — снять viewshed и overlay.</li>
                      </ul>
                    </SectionHelpTooltip>
                  </div>
                  <label className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="text-muted-foreground">Радиус, м</span>
                    <Input
                      type="number"
                      min={10}
                      className="h-7 w-24"
                      value={viewshedRadiusM}
                      onChange={(e) => setViewshedRadiusM(Number(e.target.value) || 10)}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="text-muted-foreground">AGL, м</span>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      className="h-7 w-24"
                      value={viewshedAglM}
                      onChange={(e) => setViewshedAglM(Number(e.target.value) || 0)}
                    />
                  </label>
                  <p className="text-[10px] text-muted-foreground">
                    {!viewshedObserver
                      ? "Кликните точку наблюдателя"
                      : viewshedResult?.flatEarth
                        ? "Плоская земля (нет DEM)"
                        : `Зона до ${viewshedResult?.radiusM ?? viewshedRadiusM} м`}
                  </p>
                </div>
              )}
              {activePluginId === "measure" && (
                <div className="space-y-1 px-1 pt-1">
                  <div className="flex items-center gap-1">
                    <p className="text-[10px] font-medium">Измерение</p>
                    <SectionHelpTooltip label="Справка: измерение">
                      <p>
                        Клиентское измерение на карте доски (тот же движок, что на главной
                        карте). Не создаёт объекты обстановки.
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        <li>Линия — длина и азимут сегмента; двойной клик завершает.</li>
                        <li>Полигон — площадь и периметр.</li>
                        <li>Круг — центр, затем радиус.</li>
                        <li>Точка — координаты.</li>
                        <li>Esc — снять инструмент.</li>
                      </ul>
                    </SectionHelpTooltip>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(
                      [
                        ["line", "Линия"],
                        ["polygon", "Площадь"],
                        ["circle", "Круг"],
                        ["point", "Точка"],
                      ] as const
                    ).map(([id, label]) => (
                      <Button
                        key={id}
                        type="button"
                        size="sm"
                        variant={measureMode === id ? "default" : "outline"}
                        className="h-7 px-2 text-[10px]"
                        onClick={() => {
                          setMeasureMode(id);
                          setMeasureLabel(null);
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  {measureLabel && (
                    <pre className="whitespace-pre-wrap rounded border bg-muted/40 p-1.5 font-mono text-[10px] leading-snug">
                      {measureLabel}
                    </pre>
                  )}
                </div>
              )}
              {activePluginId === "buffer" && (
                <div className="space-y-1 px-1 pt-1">
                  <label className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="text-muted-foreground">Буфер, м</span>
                    <Input
                      type="number"
                      min={1}
                      className="h-7 w-24"
                      value={bufferRadiusM}
                      onChange={(e) => setBufferRadiusM(Number(e.target.value) || 1)}
                    />
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 w-full text-xs"
                    disabled={!canManagePerm || selectedId == null || createFeature.isPending}
                    onClick={() => {
                      if (boardId == null || selectedId == null) {
                        toast({ title: "Выберите фичу на карте", variant: "destructive" });
                        return;
                      }
                      const feat = features.find(
                        (f) => Number(f.properties?.id) === selectedId,
                      );
                      if (!feat) {
                        toast({ title: "Фича не найдена", variant: "destructive" });
                        return;
                      }
                      const ring = buildBufferPolygon(feat, bufferRadiusM);
                      if (!ring || ring.length < 4) {
                        toast({
                          title: "Не удалось построить буфер",
                          variant: "destructive",
                        });
                        return;
                      }
                      const coords = ring.slice(0, -1) as [number, number][];
                      createFeature.mutate({
                        kind: "polygon",
                        coordinates: coords,
                        stroke_color: "#2563eb",
                        fill_color: "#2563eb",
                        label: `Буфер ${bufferRadiusM} м`,
                      });
                      toast({ title: "Буфер создан" });
                    }}
                  >
                    Создать буфер
                  </Button>
                </div>
              )}
              {snapEnabled && (
                <div className="space-y-1 px-1 pt-1">
                  <label className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="text-muted-foreground">Snap, м</span>
                    <Input
                      type="number"
                      min={1}
                      className="h-7 w-20"
                      value={snapToleranceM}
                      onChange={(e) => setSnapToleranceM(Number(e.target.value) || 1)}
                      aria-label="Допуск snap в метрах"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={snapToMvt}
                      onChange={(e) => setSnapToMvt(e.target.checked)}
                      aria-label="Snap к объектам ОИ на подложке"
                    />
                    к объектам ОИ (MVT)
                  </label>
                </div>
              )}
            </div>

            </>
          }
          overlays={
            <>
              {boardId != null && <TacticalReferenceLayersPanel />}
              {boardId != null && (
                <TacticalHeatmapPanel
                  features={features}
                  tracks={tracksQuery.data ?? []}
                />
              )}
            </>
          }
        />

      </div>

      <AlertDialog open={deleteBoardOpen} onOpenChange={setDeleteBoardOpen}>
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить доску?</AlertDialogTitle>
            <AlertDialogDescription>
              Доска «{board?.name}» и все её объекты будут удалены. Каталог ОИ не изменится.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBoard.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteBoard.isPending || boardId == null}
              onClick={(e) => {
                e.preventDefault();
                if (boardId != null) deleteBoard.mutate(boardId);
              }}
            >
              {deleteBoard.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={memberToRemove != null}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      >
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить участника?</AlertDialogTitle>
            <AlertDialogDescription>
              Пользователь {memberToRemove} потеряет явный доступ к доске (если нет доступа
              по организации или как создатель).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMember.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removeMember.isPending || memberToRemove == null}
              onClick={(e) => {
                e.preventDefault();
                if (memberToRemove != null) removeMember.mutate(memberToRemove);
              }}
            >
              {removeMember.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={trackToDelete != null}
        onOpenChange={(open) => {
          if (!open) setTrackToDelete(null);
        }}
      >
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить трек?</AlertDialogTitle>
            <AlertDialogDescription>
              Трек и все его точки будут удалены с доски. CAD-фичи не изменятся.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTrack.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTrack.isPending || trackToDelete == null}
              onClick={(e) => {
                e.preventDefault();
                if (trackToDelete != null) deleteTrack.mutate(trackToDelete);
              }}
            >
              {deleteTrack.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={regionToDelete != null}
        onOpenChange={(open) => {
          if (!open) setRegionToDelete(null);
        }}
      >
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить регион basemap?</AlertDialogTitle>
            <AlertDialogDescription>
              Метаданные и кэш тайлов региона будут удалены с этого устройства.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={regionToDelete == null}
              onClick={(e) => {
                e.preventDefault();
                if (regionToDelete == null) return;
                const id = regionToDelete;
                void removeBasemapRegion(id)
                  .then(() => {
                    setRegionToDelete(null);
                    void qc.invalidateQueries({ queryKey: ["tactical", "basemap-regions"] });
                    toast({ title: "Регион удалён" });
                  })
                  .catch(() =>
                    toast({ title: "Не удалось удалить регион", variant: "destructive" }),
                  );
              }}
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={importPayloads != null}
        onOpenChange={(open) => {
          if (!open && !importBusy) setImportPayloads(null);
        }}
      >
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Импортировать фичи?</AlertDialogTitle>
            <AlertDialogDescription>
              Источник: {importSourceLabel}. Будет создано {importPayloads?.length ?? 0}{" "}
              объектов на текущей доске (добавление, без удаления существующих). История и
              realtime сработают как при обычном создании.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importBusy}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={importBusy || boardId == null || !importPayloads?.length}
              onClick={(e) => {
                e.preventDefault();
                if (boardId == null || !importPayloads?.length) return;
                const payloads = importPayloads;
                const id = boardId;
                setImportBusy(true);
                void (async () => {
                  let ok = 0;
                  let fail = 0;
                  try {
                    for (const payload of payloads) {
                      try {
                        await tacticalApi.createFeature(id, payload);
                        ok += 1;
                      } catch {
                        fail += 1;
                      }
                    }
                    invalidateBoard(id);
                    setImportPayloads(null);
                    toast({
                      title:
                        fail === 0
                          ? `Импорт: создано ${ok}`
                          : `Импорт: создано ${ok}, ошибок ${fail}`,
                      variant: fail > 0 && ok === 0 ? "destructive" : "default",
                    });
                  } finally {
                    setImportBusy(false);
                  }
                })();
              }}
            >
              {importBusy ? "Импорт…" : "Импортировать"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </ModuleMapUiStubProvider>
    </div>
  );
}
